import { MachiningModule } from './machining.model';

export const MACHINING_MODULES: MachiningModule[] = [
  // ── Shared foundation ────────────────────────────────────────────────
  {
    id: 'shop-safety',
    number: 1,
    // machine omitted = shared module visible in both tracks
    title: 'Shop Safety',
    subtitle: 'PPE, hazards, and the rules that keep all your fingers',
    description: 'Eye protection, loose-clothing risks, chip handling, emergency stop, and the machine-specific hazards you need to know before either machine spins up. Safety is not a separate topic — it is the prerequisite for everything else.',
    estimatedTime: '15 min',
    icon: '🦺',
    sections: [
      {
        id: 'ppe',
        title: 'PPE — what you wear before the machine starts',
        content: `
          <p>Personal protective equipment is non-negotiable. None of it is optional, and "I'm just doing one quick thing" is exactly when injuries happen.</p>
          <ul>
            <li><strong>Safety glasses always</strong>, even when you are just measuring. Chips can fly without warning from adjacent machines.</li>
            <li><strong>No rings, watches, or loose sleeves.</strong> No gloves while the spindle is rotating — gloves can be caught and dragged in.</li>
            <li><strong>Long hair tied back.</strong> Drawstrings tucked or removed.</li>
            <li><strong>Closed-toe shoes minimum;</strong> safety toes are better. A dropped chuck weighs enough to break a foot.</li>
          </ul>
        `,
        callout: {
          type: 'warning',
          title: 'Things that get you hurt',
          text: 'Rings, watches, ties, gloves on a running spindle, loose long hair, untied apron strings, exposed toes. Every one of these has a documented incident behind it.',
        },
      },
      {
        id: 'osha',
        title: 'What the OSHA standard actually says',
        content: `
          <p>OSHA 1910.212 requires that "one or more methods of machine guarding shall be provided to protect the operator and other employees in the machine area from hazards such as those created by point of operation, ingoing nip points, rotating parts, flying chips and sparks." <a href="https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.212" target="_blank" rel="noopener">[OSHA 1910.212(a)(1)]</a></p>
          <p>The point-of-operation guard must be designed "as to prevent the operator from having any part of his body in the danger zone." Don't remove guards. Don't bypass interlocks.</p>
          <p>For bench grinders, OSHA 1910.215 requires the work rest within 1/8″ of the wheel and the tongue guard within 1/4″. Inspect the wheel (the "ring test") before mounting it. <a href="https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.215" target="_blank" rel="noopener">[OSHA 1910.215]</a></p>
        `,
        callout: {
          type: 'fact',
          title: 'OSHA 1910.212(a)(1) — verbatim',
          text: 'One or more methods of machine guarding shall be provided to protect the operator and other employees in the machine area from hazards such as those created by point of operation, ingoing nip points, rotating parts, flying chips and sparks.',
        },
      },
      {
        id: 'lathe-hazards',
        title: 'Lathe-specific hazards',
        content: `
          <ul>
            <li><strong>The chuck key.</strong> If you turn the spindle on with the chuck key in the chuck, the key becomes a projectile that has killed and maimed people. The rule is absolute: never let go of the chuck key until it is out of the chuck and physically on the bench. <a href="https://ehs.cornell.edu/campus-health-safety/occupational-safety/tool-and-machine-safety/metalworking-lathe-safety" target="_blank" rel="noopener">[Cornell EHS]</a></li>
            <li><strong>Workpiece whipping.</strong> Long stock past the headstock must be supported (steady rest, follower, or shorter stock).</li>
            <li><strong>Chip stream direction.</strong> Long stringy chips are sharp and hot. Use a chip breaker, point chips down and away, and stop the spindle to clear them when they get unmanageable. Cornell EHS adds: never bare hands.</li>
            <li><strong>Don't reach over a rotating chuck</strong> to measure or feel the work. Stop the spindle.</li>
          </ul>
        `,
        callout: {
          type: 'warning',
          title: 'The chuck key',
          text: 'Multiple university shop safety documents call this out as the single most common preventable injury at the lathe. The fix is a habit: physically grasp the key the entire time it is in the chuck. Drop it on the bench before any spindle command.',
        },
      },
      {
        id: 'mill-hazards',
        title: 'Mill-specific hazards',
        content: `
          <ul>
            <li><strong>Spindle direction.</strong> End mills are directional. Wrong direction at speed will pull a stuck tool or wreck the part. Verify before starting.</li>
            <li><strong>Workholding.</strong> A mill applies horizontal force; a vise that is "snug" is not enough. Tighten with the real handle, set parallels, confirm the work doesn't twist when you push it.</li>
            <li><strong>Table travel awareness.</strong> A power-fed table will not stop because you are in its way. Know where it is going before you engage feed.</li>
            <li><strong>Edge finders and wigglers.</strong> Spin them up slowly; engaging at full RPM can throw the tool.</li>
          </ul>
        `,
        callout: {
          type: 'warning',
          title: 'Stuck end mills',
          text: 'A bound or jammed end mill at speed can launch tooling. If a cut suddenly loads up, stop the spindle immediately — do not try to power through.',
        },
      },
      {
        id: 'what-good-looks-like',
        title: 'What good looks like',
        content: `
          <ul>
            <li>Tools laid out before you start. Workpiece secure. Chuck key on the bench. Chip tray clear.</li>
            <li>Hands resting where they can move quickly to the e-stop. Eyes on the cut, not your phone.</li>
            <li>One operation at a time. If something feels wrong, stop and reset.</li>
          </ul>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue',
          text: 'If you find yourself talking to someone, looking away, or reaching across a running spindle, you have lost the safety thread. Power off. Reset. Pause until you can give the cut your full attention.',
        },
      },
    ],
    quiz: [
      {
        id: 'gloves',
        type: 'multiple-choice',
        question: 'When is it acceptable to wear gloves while the spindle is running?',
        options: [
          'When the work is hot and you need to handle it briefly',
          'When the chuck has soft jaws',
          'When you are taking a light finishing cut',
          'Never',
        ],
        correctIndex: 3,
        explanation: 'Gloves can be caught by rotating work and pull your hand into the machine. Stop the spindle, then handle hot work with a rag or tongs.',
      },
      {
        id: 'chuck-key',
        type: 'true-false',
        question: 'The chuck key may stay in the chuck briefly while you grab the next part.',
        options: ['True', 'False'],
        correctIndex: 1,
        explanation: 'The rule is absolute: the chuck key never leaves your hand while it is in the chuck. A spindle command with the key in is a projectile launch.',
      },
      {
        id: 'chip-clearing',
        type: 'multiple-choice',
        question: 'The proper way to clear chips from the work and machine is to:',
        options: [
          'Brush them away by hand once the cut is finished',
          'Use compressed air to blow them clear',
          'Stop the spindle, then use a stiff-bristle brush',
          'Vacuum them while the spindle is still running',
        ],
        correctIndex: 2,
        explanation: 'Stop the spindle first. Compressed air drives chips into eyes and packs them into the ways; bare hands meet sharp, hot edges.',
      },
      {
        id: 'osha-guards',
        type: 'multiple-choice',
        question: 'OSHA 1910.212 requires machine guards to protect against which hazards?',
        options: [
          'Point of operation only',
          'Point of operation and ingoing nip points only',
          'Rotating parts only',
          'Point of operation, ingoing nip points, rotating parts, and flying chips/sparks',
        ],
        correctIndex: 3,
        explanation: 'The standard names all four hazard categories. Don\'t remove or bypass guards "just for one cut."',
      },
    ],
    videos: [
      {
        title: 'Metal Lathe Tutorial 1: Theory',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks Metal Lathe Tutorial 1 Theory',
        description: 'Quinn Dunki\'s introduction covers the basic operating philosophy, including safety habits. A solid foundation before you touch the controls.',
      },
      {
        title: 'Bridgeport Mill Safety & Tour',
        channel: 'mrpete222 / Tubalcain',
        searchQuery: 'Tubalcain mrpete222 Bridgeport mill safety tour',
        description: 'Tubalcain is a retired shop teacher; his Bridgeport tours are the closest thing to a first-day-in-shop-class you can get on YouTube.',
      },
    ],
    sources: [
      {
        label: 'OSHA 1910.212 — General requirements for all machines',
        url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.212',
      },
      {
        label: 'OSHA 1910.215 — Abrasive wheel machinery',
        url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.215',
      },
      {
        label: 'Cornell EHS — Metalworking Lathe Safety',
        url: 'https://ehs.cornell.edu/campus-health-safety/occupational-safety/tool-and-machine-safety/metalworking-lathe-safety',
      },
      {
        label: 'University of Michigan EHS — Mill Safety (PDF)',
        url: 'https://ehs.umich.edu/wp-content/uploads/2018/08/Mill-Safety.pdf',
      },
      {
        label: 'LSU AMMF — Lathe Safety Rules',
        url: 'https://www.lsu.edu/eng/mie/cuf/ammf/safetyrules/Lathes.php',
      },
    ],
    toolsNeeded: [
      'Safety glasses meeting ANSI Z87.1 — required at all times in the shop',
      'Closed-toe leather shoes (composite or steel toe preferred)',
      'Hearing protection (earplugs or muffs) for any sustained machining',
      'A stiff-bristle chip brush — never bare hands, never compressed air at the machine',
      'A face shield for grinding or any operation that throws chips toward your face',
    ],
    preChecklist: [
      'Safety glasses on, hair back, no loose clothing or jewelry',
      'Closed-toe shoes (safety toe preferred)',
      'Workpiece secure: chuck tight (lathe) or vise + parallels confirmed (mill)',
      'Chuck key out of the chuck and on the bench (lathe) / wrenches off the table (mill)',
      'Tooling sharp, mounted firmly, at correct height',
      'Coolant tray clear of stray tools, rags, or stock',
      'You know where the e-stop is and can reach it from where you stand',
    ],
    commonMistakes: [
      'Chuck key left in the chuck — the single most common preventable shop accident.',
      'Wearing gloves while the spindle runs — gloves get caught and pull your hand in.',
      'Using compressed air at the machine — drives chips into eyes and packs them into the ways.',
      'Removing or bypassing a guard "just for one cut."',
      'Measuring or adjusting work while the spindle is rotating.',
    ],
    practiceExercises: [
      {
        id: 'find-the-estop',
        description: 'Stand at your normal operating position. Without looking, identify the emergency stop and reach for it. If it took longer than half a second, reorganise the tools and posture so it does not.',
        hint: 'Test on both machines if you have both — the e-stop placement may differ.',
      },
      {
        id: 'spindle-baseline',
        description: 'Run the spindle at a typical RPM with no work mounted. Listen and feel. This is your "normal" — any deviation mid-cut is a signal to stop.',
        hint: 'Run at the speeds you actually use, not the maximum. You are calibrating your ear to your normal operating range.',
      },
      {
        id: 'chuck-key-drill',
        description: 'Mount and remove the chuck key three times in a row on the lathe. Never let go until it is fully clear of the chuck and on the bench. Build the habit before you need it.',
        hint: 'The hand carrying the key is the hand that proves the spindle is safe to start.',
      },
    ],
  },
  {
    id: 'measuring',
    number: 2,
    title: 'Measuring & Inspection',
    subtitle: 'Calipers, micrometers, indicators, and basic layout',
    description: 'Read a caliper to 0.001″, a micrometer to 0.0001″, set up an indicator, and lay out work with a scribe and surface plate. These are the senses you use to verify every cut you make — without them, a "good" surface is just a guess.',
    estimatedTime: '30 min',
    icon: '📏',
    sections: [
      {
        id: 'why-precision',
        title: 'Why precision matters and how to treat the tools',
        content: `
          <p>You cannot evaluate a cut you cannot measure. Every diameter you turn, every depth you mill, every clearance you fit is judged against a number — and that number comes from a precision instrument that respects no estimation.</p>
          <p>Precision measuring tools are also fragile. A 0-1″ micrometer dropped onto concrete is no longer a 0-1″ micrometer; it is scrap. A caliper used to pry a stuck part is now an inaccurate caliper that you will trust by habit. Treat the instruments the way you would treat the workpiece you are trying to verify.</p>
          <ul>
            <li><strong>Clean before, clean after.</strong> A single chip caught between caliper jaws will read 0.005″ heavy.</li>
            <li><strong>Store mics with the spindle open</strong> a few thousandths so the anvil and spindle faces are not in metal-to-metal contact.</li>
            <li><strong>Surface plate is for measuring, not staging.</strong> Do not set hot parts, tools with sharp edges, or coffee on it.</li>
            <li><strong>Zero before every session.</strong> Calipers closed, mic closed — read the indicated value. If it is not zero, you have an offset to remember (or a tool to recalibrate).</li>
          </ul>
        `,
        callout: {
          type: 'warning',
          title: 'Respect the instruments',
          text: 'A dropped micrometer, a chip in a caliper jaw, or a scratched surface plate is a precision tool you no longer own. Slow down. Set things on a clean cloth, not metal. Two seconds of care saves a $200 instrument.',
        },
      },
      {
        id: 'reading-caliper',
        title: 'Reading a caliper to 0.001″',
        content: `
          <p>A vernier or dial caliper resolves to 0.001″ (one thousandth of an inch). A digital caliper shows it directly. Vernier and dial require you to combine two readings: the main scale (the inches and tenths) plus the vernier or dial reading (the thousandths). <a href="https://www.mitutoyo.com/webfoo/wp-content/uploads/E11003-Quick-Guide.pdf" target="_blank" rel="noopener">[Mitutoyo Quick Guide]</a></p>
          <p><strong>The procedure:</strong></p>
          <ol>
            <li>Wipe both jaws and the workpiece. Close the jaws against each other gently.</li>
            <li>Check the zero. Vernier: the zero on the sliding scale must align with the zero on the main scale. Dial: needle on 0.000. Digital: press zero.</li>
            <li>Open the jaws and place the work between them. Close gently — do not squeeze. Just enough contact that the work does not fall out.</li>
            <li>Read the main scale to the nearest tenth (0.100″ marks).</li>
            <li>Add the vernier or dial reading (0.000 to 0.100″).</li>
            <li>Repeat once more on the same feature. The two readings should agree within 0.001″.</li>
          </ol>
          <p>Calipers are reliable to about ±0.001″ in skilled hands. They are <em>not</em> the right tool for tighter tolerances; use a micrometer for anything tighter than ±0.002″. <a href="https://ocw.mit.edu/courses/2-670-mechanical-engineering-tools-january-iap-2004/8d23a77bb35a7bf17898905fcbb433a0_measure.pdf" target="_blank" rel="noopener">[MIT 2.670]</a></p>
        `,
      },
      {
        id: 'reading-mic',
        title: 'Reading a micrometer to 0.0001″',
        content: `
          <p>A 0-1″ outside micrometer uses a precision 40-TPI screw on its spindle. One full turn of the thimble advances the spindle 0.025″. The thimble itself is divided into 25 graduations — each 0.001″. A vernier micrometer adds a third scale on the sleeve that resolves the final 0.0001″. <a href="https://www.me.psu.edu/cimbala/me345/Lectures/Labs/Basic_statistics/How_to_read_micrometer.pdf" target="_blank" rel="noopener">[Penn State ME 345]</a></p>
          <p><strong>The procedure:</strong></p>
          <ol>
            <li>Wipe the anvil and spindle faces. Close the mic with the ratchet stop (the small knurled knob at the end) — feel it click two or three times. Read zero.</li>
            <li>Open the spindle. Place the work between anvil and spindle.</li>
            <li>Close with the <em>ratchet stop only</em>, not the thimble. The ratchet limits clamping force so every measurement uses the same pressure. Two or three clicks.</li>
            <li>Read: largest visible whole tenth on the sleeve (0.100″ marks), plus the small divisions (0.025″ each), plus the thimble reading (0.001″ each), plus the vernier (0.0001″) if present. Sum them.</li>
            <li>Repeat. Agreement within 0.0001″ on a vernier mic confirms technique.</li>
          </ol>
        `,
        callout: {
          type: 'tip',
          title: 'The ratchet stop is not optional',
          text: 'Cranking the thimble by hand applies whatever force your wrist feels like applying — usually too much, and not the same twice. The ratchet enforces a calibrated clamping pressure (~5-10 N). Skip it and you are measuring how tightly you squeezed, not the part.',
        },
      },
      {
        id: 'indicators',
        title: 'Dial indicators: zero, deflect, read',
        content: `
          <p>A dial indicator does not measure absolute size — it measures <em>change</em> from a reference. Used with a magnetic base or stand, it tells you how much a surface deviates from a known plane (flatness, runout, parallelism, or "is this 0.0001″ taller than that").</p>
          <p><strong>The procedure:</strong></p>
          <ol>
            <li>Mount the indicator on a stable base. The base must not rock — if it does, your reading does not exist.</li>
            <li>Bring the contact tip down onto the reference surface until the dial deflects about half its travel. This is your zero.</li>
            <li>Loosen the bezel and rotate it so the needle aligns with the zero mark.</li>
            <li>Move the work (or the indicator) and read the deflection. Plus indicates rise; minus indicates drop.</li>
          </ol>
          <p>For runout on a lathe, mount the indicator to the carriage or tailstock, rest it on the work, and rotate the spindle by hand. Total indicator reading (TIR) is the swing from highest to lowest — that is the runout. <a href="https://www.nims-skills.org/sites/default/files/media/document/NIMS%20Machining%20Level%20I%20Standards.pdf" target="_blank" rel="noopener">[NIMS Level I]</a></p>
        `,
      },
      {
        id: 'layout-and-good',
        title: 'Layout basics and what good looks like',
        content: `
          <p>Layout is the act of marking a workpiece with the lines, holes, and edges you intend to cut. The classic toolkit is a scribe, a square, layout dye (Dykem blue), and a surface plate or known flat reference.</p>
          <ul>
            <li><strong>Spray or paint the workpiece</strong> with layout dye where you intend to scribe. The blue gives the scribe line contrast.</li>
            <li><strong>Use a square against a finished edge</strong> — never against an as-sawn or as-cast edge — to project your reference.</li>
            <li><strong>Scribe lightly.</strong> A scribe is a marking tool, not a cutting tool. A heavy line is harder to cut to than a faint one.</li>
            <li><strong>Center-punch hole locations</strong> before drilling so the drill point lands where you marked, not where it wandered to.</li>
          </ul>
          <p>The point of layout is to make the next decision visible. You can always cut to a line. You cannot cut to "about here."</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue',
          text: 'You have read the tool correctly when two consecutive measurements of the same feature agree to the tool\'s resolution — and when the tool itself reads zero on a known reference. If either fails, the variable is technique, not the part.',
        },
      },
    ],
    quiz: [
      {
        id: 'mic-thimble-advance',
        type: 'multiple-choice',
        question: 'How far does the spindle of a standard 0-1″ outside micrometer advance per full turn of the thimble?',
        options: [
          '0.001″',
          '0.010″',
          '0.025″',
          '0.100″',
        ],
        correctIndex: 2,
        explanation: 'The spindle screw is a precision 40-TPI thread. One full turn = 1/40 inch = 0.025″. The thimble has 25 graduations, each representing 0.001″.',
      },
      {
        id: 'caliper-offset',
        type: 'true-false',
        question: 'A dial caliper that reads 0.001″ off when closed is fine to use — you can just subtract the offset.',
        options: ['True', 'False'],
        correctIndex: 1,
        explanation: 'No. A caliper that does not zero is telling you something is wrong (chips on the jaws, wear, damage). Zero it physically — clean the jaws, recalibrate, or set it aside. Mental subtractions get forgotten exactly when accuracy matters.',
      },
      {
        id: 'ratchet-purpose',
        type: 'multiple-choice',
        question: 'On an outside micrometer, the ratchet stop exists to:',
        options: [
          'Lock the spindle so it cannot move while you read',
          'Limit the spindle force to a consistent, calibrated pressure',
          'Advance the spindle faster than hand-turning the thimble',
          'Indicate when the spindle is at zero',
        ],
        correctIndex: 1,
        explanation: 'The ratchet slips at a fixed torque (~5-10 N of force at the anvil). It enforces repeatable clamping pressure across measurements. Hand-cranking applies whatever force your wrist feels like — usually too much, and never the same twice.',
      },
      {
        id: 'caliper-vs-mic',
        type: 'multiple-choice',
        question: 'You need to verify a diameter is 0.5000″ ± 0.0005″. Which tool is appropriate?',
        options: [
          'Steel rule',
          'Vernier caliper',
          '0-1″ outside micrometer',
          'Tape measure',
        ],
        correctIndex: 2,
        explanation: 'A caliper resolves to 0.001″ — the same order of magnitude as the tolerance. A micrometer resolves to 0.0001″, giving you a factor of 10 safety margin. Match tool resolution to tolerance: roughly 10× tighter than the spec.',
      },
      {
        id: 'indicator-base',
        type: 'multiple-choice',
        question: 'You set up a dial indicator on a magnetic base to check runout on a lathe. The base rocks slightly when you push on it. You should:',
        options: [
          'Take the reading anyway, the rocking is small',
          'Average several readings to cancel the rocking',
          'Stop and reseat the base — a rocking base invalidates the measurement',
          'Re-zero the indicator with the base in its rocked position',
        ],
        correctIndex: 2,
        explanation: 'A base that moves is not a base. Every dial-indicator reading is measured against the assumption that the base is fixed. If it is not, your "reading" is some unknown combination of part deviation and base shift. Reseat, clean the magnetic surface, or relocate.',
      },
    ],
    videos: [
      {
        title: 'How to Read a Micrometer',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks how to read a micrometer',
        description: 'Quinn walks through reading a standard 0-1″ mic clearly and slowly. The best beginner-friendly intro to the technique on YouTube.',
      },
      {
        title: 'Precision Measurement Fundamentals',
        channel: 'Joe Pieczynski',
        searchQuery: 'Joe Pieczynski precision measurement micrometer caliper',
        description: 'Joe Pie\'s precision-measurement videos cover the small technique details (ratchet use, thermal effects, proper handling) that separate "I read 0.500" from "I know this part is 0.5002".',
      },
    ],
    sources: [
      {
        label: 'Mitutoyo — Quick Guide to Precision Measuring Instruments (PDF)',
        url: 'https://www.mitutoyo.com/webfoo/wp-content/uploads/E11003-Quick-Guide.pdf',
      },
      {
        label: 'Mitutoyo — How to Read a Vernier Micrometer',
        url: 'https://www.mitutoyo.com/educational-resource/how-to-read-a-vernier-micrometer/',
      },
      {
        label: 'MIT OpenCourseWare 2.670 — Measurement Introduction (PDF)',
        url: 'https://ocw.mit.edu/courses/2-670-mechanical-engineering-tools-january-iap-2004/8d23a77bb35a7bf17898905fcbb433a0_measure.pdf',
      },
      {
        label: 'Penn State ME 345 — How to Read an Outside Micrometer (PDF)',
        url: 'https://www.me.psu.edu/cimbala/me345/Lectures/Labs/Basic_statistics/How_to_read_micrometer.pdf',
      },
      {
        label: 'NIMS Machining Level I — Measurement, Materials, and Safety Prep Guide (PDF)',
        url: 'https://www.in.gov/dwd/files/industry-certifications/NIMS-Machining-Level-1-Measurement-Materials-and-Safety.pdf',
      },
    ],
    toolsNeeded: [
      'Dial or digital caliper, 0-6″ (0.001″ resolution minimum)',
      '0-1″ outside micrometer (0.0001″ vernier preferred for finishing work)',
      'Dial indicator or dial test indicator with magnetic base',
      'Granite surface plate (any small shop-grade plate, kept clean and covered when not in use)',
      'Scribe, machinist\'s square, layout dye (Dykem blue), center punch',
      'Optional: 1″ gauge block or ground reference pin for calibration checks',
    ],
    preChecklist: [
      'Hands and tools clean and dry — no chips, no coolant, no skin oil on critical surfaces',
      'Caliper closed reads 0.000 — if not, clean the jaws and re-check before measuring anything',
      'Micrometer closed reads 0.0000 with the ratchet stop — same rule',
      'Indicator base is stable on the surface (no rocking when you push on it)',
      'Workpiece is clean, deburred, and at room temperature (hot parts are bigger parts)',
      'Surface plate is wiped down and free of debris before laying anything on it',
    ],
    commonMistakes: [
      'Skipping the zero check at the start of the session. The single most common source of wrong measurements.',
      'Squeezing the caliper too hard, deforming soft or thin work and reading low.',
      'Spinning the micrometer thimble by hand instead of using the ratchet stop — gives inconsistent clamping pressure.',
      'Reading the wrong vernier line on a vernier mic (off by 0.0001″ — small but it adds up).',
      'Measuring hot work fresh off the lathe. Steel expands roughly 0.00065″ per inch per 100 °F rise.',
      'Storing precision tools dirty or with closed faces in contact. Both wear the reference surfaces.',
    ],
    practiceExercises: [
      {
        id: 'zero-check-routine',
        description: 'Run the full zero-check routine on every precision instrument you own: caliper closed reads 0.000, mic closed reads 0.0000 with the ratchet, indicator deflects smoothly through its range. Note any tool that fails — that is your next purchase or service item.',
        hint: 'Do this at the start of every session. It is the single most reliable habit you can build.',
      },
      {
        id: 'known-reference-cross-check',
        description: 'Measure a known reference (1″ gauge block, ground dowel pin, or any precision-ground item with a known dimension) on both your caliper and your micrometer. They should agree with the reference within their respective resolutions. If they do not, one of them is lying.',
        hint: 'If you do not own a gauge block, a fresh ground dowel pin from a hardware store is typically held to ±0.0002″ and is good enough for a comparison.',
      },
      {
        id: 'repeatability-drill',
        description: 'Pick any feature on a workpiece. Measure it three times in a row without moving the part. If the three readings disagree by more than the tool\'s resolution, you are the variable — slow down, reset technique, repeat.',
        hint: 'Repeatability is technique; accuracy is calibration. Master repeatability first; calibration only matters once you can read the same number twice.',
      },
    ],
    unlockAfterModule: 'shop-safety',
  },

  // ── Lathe track ──────────────────────────────────────────────────────
  {
    id: 'lathe-anatomy',
    number: 3,
    machine: 'lathe',
    title: 'Lathe Anatomy & Controls',
    subtitle: 'Headstock, tailstock, carriage, cross-slide, compound, DRO',
    description: 'Before you spin up a workpiece you need to know what every casting on the lathe is called and what each handwheel controls. This is the vocabulary every subsequent module assumes you already speak.',
    estimatedTime: '20 min',
    icon: '⚙️',
    sections: [
      {
        id: 'overview',
        title: 'The major castings — a walk-around',
        content: `
          <p>A manual engine lathe has four major castings you must be able to name and point to without thinking: the <strong>bed</strong>, the <strong>headstock</strong>, the <strong>tailstock</strong>, and the <strong>carriage assembly</strong>. Everything else hangs off one of these four. <a href="https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf" target="_blank" rel="noopener">[Army TC 9-524 Ch. 7]</a></p>
          <ul>
            <li><strong>Bed.</strong> The long horizontal casting that everything else rides on. The polished V-grooves along the top are the <em>ways</em> — keep them clean, never set tools on them.</li>
            <li><strong>Headstock.</strong> The fixed casting on the left. Houses the spindle, the gear train, and the speed-change controls.</li>
            <li><strong>Tailstock.</strong> The casting on the right that slides along the ways and clamps in place. Supports the free end of long work and holds drills, reamers, or centers.</li>
            <li><strong>Carriage assembly.</strong> The H-shaped casting that straddles the bed. It carries the cross-slide, compound rest, and tool post — the parts that actually make the cut.</li>
          </ul>
          <p>If you can stand at the operator's position and name each of these four out loud while pointing, you have the foundation. Everything else is a sub-component of one of them. <a href="https://www.usi.edu/science/engineering/machasst/englathe/elath1.htm" target="_blank" rel="noopener">[USI Engineering — Parts of the Engine Lathe]</a></p>
        `,
        callout: {
          type: 'warning',
          title: 'Pinch points and moving parts',
          text: 'During this walk-around the spindle is OFF and the machine is unplugged or locked out. Once it is running, the carriage moves under power, the spindle nose can grab loose sleeves, and the leadscrew turns the entire length of the bed. Identify these moving surfaces now so you know what not to lean on later.',
        },
      },
      {
        id: 'headstock',
        title: 'Headstock, spindle, and chuck',
        content: `
          <p>The headstock is the lathe\'s engine room. Inside is the <strong>spindle</strong> — the rotating shaft that grips the workpiece — driven by a motor through a gearbox or belt-and-pulley arrangement. The headstock also houses the speed-change controls (the gear levers or variable-speed dial) and the threading/feed gear train. <a href="https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf" target="_blank" rel="noopener">[Army TC 9-524]</a></p>
          <p>At the front of the spindle is the <strong>spindle nose</strong> — the threaded or cammed end where workholding mounts. The 3-jaw scroll chuck is the most common starter; collets, faceplates, and 4-jaw chucks all use the same spindle nose. Workholding is its own module — for now just know where it attaches.</p>
          <p><strong>Spindle speed.</strong> Set by the gear levers (on a geared-head lathe) or the variable-speed dial (on a VFD-driven lathe). The chosen RPM matters for every cut — Speeds & Feeds is a whole module later. For now: do not change speed while the spindle is running unless your machine explicitly allows it.</p>
        `,
      },
      {
        id: 'carriage',
        title: 'Carriage, cross-slide, and compound',
        content: `
          <p>The carriage assembly is where cuts happen. Three handwheels control three independent motions:</p>
          <ol>
            <li><strong>The apron handwheel</strong> moves the entire carriage left or right along the bed (the <strong>Z-axis</strong>). This is your longitudinal feed — what produces a turned diameter.</li>
            <li><strong>The cross-slide handwheel</strong> moves the cross-slide in or out, perpendicular to the spindle axis (the <strong>X-axis</strong>). This is what advances the tool into the work and what produces a faced end.</li>
            <li><strong>The compound rest handwheel</strong> moves the compound at whatever angle you have swiveled it to. Set to 29.5° it is used for threading; set to other angles it cuts tapers and chamfers. Its dial reads the actual movement of the compound, not the depth into the part.</li>
          </ol>
          <p>The <strong>apron</strong> also houses two power-feed controls: a feed lever (engages the feed rod for automatic carriage travel during turning) and a <strong>half-nut lever</strong> (engages the leadscrew for threading). These are different mechanisms — the half-nut moves the carriage at threading speed, much faster than a normal feed. Engage the wrong one and your finishing pass becomes a thread. <a href="https://tricities.wsu.edu/wp-content/uploads/WSUTC_Lathe-Operation-2-3-2018.pdf" target="_blank" rel="noopener">[WSU Tri-Cities SOP]</a></p>
        `,
        callout: {
          type: 'warning',
          title: 'Apron handwheel vs cross-slide handwheel',
          text: 'Beginners reflexively grab the wrong handwheel under pressure. The apron is the big one on the front face — moves carriage left/right. The cross-slide is on top of the carriage, perpendicular to it — moves the tool toward/away from the operator. Practice reaching for each by name (eyes closed counts) until the habit is automatic.',
        },
      },
      {
        id: 'tailstock',
        title: 'Tailstock and accessories',
        content: `
          <p>The <strong>tailstock</strong> slides along the ways on the right side of the bed. You loosen its base clamp, slide it to where you need it, and re-clamp. It has its own handwheel that advances a sliding spindle (the <strong>quill</strong>) — typically about 3-5″ of travel — with a Morse taper bore in the end. <a href="https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf" target="_blank" rel="noopener">[Army TC 9-524]</a></p>
          <p>What it does for you:</p>
          <ul>
            <li><strong>Supports long work between centers.</strong> A live center in the quill turns with the workpiece and lets you turn long stock without it whipping.</li>
            <li><strong>Holds drills and reamers</strong> with a Morse-taper drill chuck or directly with taper-shank tooling. Advance the quill by hand to drill on-axis.</li>
            <li><strong>Holds taps for threading by hand-power.</strong> A spring-loaded tap holder in the tailstock keeps a hand-cut thread coaxial with the work.</li>
          </ul>
          <p>The tailstock also has a <strong>set-over adjustment</strong> — a small lateral offset between its base and its body — used historically for cutting shallow tapers. Modern practice prefers the compound rest for tapers; ignore the set-over until you actually need it.</p>
        `,
      },
      {
        id: 'dro',
        title: 'Digital readouts (DROs) and what good looks like',
        content: `
          <p>Many lathes are fitted with a <strong>digital readout</strong> — a numerical display that shows the current position of the carriage (Z) and cross-slide (X) to high resolution, typically 0.0001″. The DRO does not change how the machine cuts; it replaces the dials on the handwheels with a numeric position that does not drift or get miscounted. <a href="https://en.wikipedia.org/wiki/Digital_readout" target="_blank" rel="noopener">[Wikipedia — DRO]</a></p>
          <p>Three concepts you must understand before using a DRO:</p>
          <ul>
            <li><strong>Absolute vs incremental mode.</strong> Absolute reads position from a single fixed zero (typically the workpiece datum you set at the start). Incremental reads position from wherever you last hit the zero key. Most DROs let you flip between them or keep both simultaneously.</li>
            <li><strong>Diameter mode (X-axis only).</strong> Lathe DROs almost always offer a diameter mode that doubles the cross-slide motion in the readout: move the slide in by 0.010″ on the radius and the X display advances by 0.020″ — the actual change in workpiece diameter. Saves you a mental "× 2" on every cut. Confirm which mode your DRO is in <em>before</em> you start cutting.</li>
            <li><strong>Zeroing on a reference.</strong> Touch the tool to a known surface (faced end, ground reference, or a piece of paper held against the work), zero the axis, and your DRO is now the truth. Make this the first thing you do on every new piece.</li>
          </ul>
          <p>If your lathe has no DRO, the same logic applies — the cross-slide dial reads radius or diameter depending on the machine, and the carriage dial is your Z. The DRO just removes the bookkeeping.</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue — DRO trust check',
          text: 'Before you trust a DRO for tight work, jog the cross-slide a known distance (say 0.500″) by the handwheel dial, and confirm the DRO moved by the same amount. If you are in diameter mode the readout will show 1.000″ instead — that is the lesson, not a bug. Do this once per session.',
        },
      },
    ],
    quiz: [
      {
        id: 'major-castings',
        type: 'multiple-choice',
        question: 'Which of these is NOT one of the four major lathe castings?',
        options: [
          'Bed',
          'Headstock',
          'Quill',
          'Carriage assembly',
        ],
        correctIndex: 2,
        explanation: 'The quill is the sliding spindle inside the tailstock — a sub-component, not a major casting. The four major castings are the bed, headstock, tailstock, and carriage assembly.',
      },
      {
        id: 'compound-purpose',
        type: 'true-false',
        question: 'The compound rest can be swiveled to any angle for cutting tapers and chamfers.',
        options: ['True', 'False'],
        correctIndex: 0,
        explanation: 'True. The compound\'s defining feature vs. the cross-slide is that it swivels. Set to 29.5° it is used for threading; at other angles it cuts tapers, chamfers, and angled features.',
      },
      {
        id: 'half-nut',
        type: 'multiple-choice',
        question: 'What does the half-nut lever on the apron do?',
        options: [
          'Engages the feed rod for automatic carriage travel during turning',
          'Engages the leadscrew for threading',
          'Locks the carriage in place',
          'Reverses spindle direction',
        ],
        correctIndex: 1,
        explanation: 'The half-nut locks onto the leadscrew, which turns much faster than the feed rod and is geared to spindle rotation for thread cutting. Engaging it accidentally during a normal cut will rip the carriage along at threading speed — destructive.',
      },
      {
        id: 'dro-diameter-mode',
        type: 'multiple-choice',
        question: 'You move the cross-slide handwheel inward by 0.010″ (a 0.010″ radial movement). Your DRO\'s X-axis reading advances by 0.020″. The DRO is:',
        options: [
          'Broken — it should match the handwheel exactly',
          'In radius mode',
          'In diameter mode',
          'In incremental mode',
        ],
        correctIndex: 2,
        explanation: 'Diameter mode doubles the cross-slide movement in the readout because that is the actual change in workpiece diameter (radial movement × 2 = diameter change). Most lathe DROs default to diameter mode for exactly this reason — it eliminates a mental ×2 on every cut.',
      },
      {
        id: 'tailstock-uses',
        type: 'multiple-choice',
        question: 'Besides supporting long work between centers, the tailstock is commonly used to:',
        options: [
          'Apply additional cutting pressure to the tool',
          'Drive the spindle in low gear',
          'Hold drills, reamers, and taps for on-axis operations',
          'Adjust spindle alignment with the bed',
        ],
        correctIndex: 2,
        explanation: 'The tailstock\'s Morse-taper bore accepts drill chucks and taper-shank tooling. Advancing the quill by hand drills holes coaxial with the spindle — one of the most common tailstock operations.',
      },
    ],
    videos: [
      {
        title: 'Metal Lathe Tutorial 2: Anatomy of the Lathe',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks metal lathe anatomy tutorial 2',
        description: 'Quinn\'s walk-around of every major lathe component with clear visuals. Watch this once with your own machine in front of you.',
      },
      {
        title: 'Lathe Basics — A Beginner\'s Guide',
        channel: 'mrpete222 / Tubalcain',
        searchQuery: 'Tubalcain mrpete222 South Bend lathe basics anatomy',
        description: 'Tubalcain\'s South Bend tours cover anatomy and controls in the context of an actual shop class lesson plan.',
      },
    ],
    sources: [
      {
        label: 'U.S. Army TC 9-524 Ch. 7 — Lathes (PDF, hosted by CMU)',
        url: 'https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf',
      },
      {
        label: 'USI Engineering — Parts of the Engine Lathe',
        url: 'https://www.usi.edu/science/engineering/machasst/englathe/elath1.htm',
      },
      {
        label: 'WSU Tri-Cities — Manual Toolroom Lathes SOP (PDF)',
        url: 'https://tricities.wsu.edu/wp-content/uploads/WSUTC_Lathe-Operation-2-3-2018.pdf',
      },
      {
        label: 'Sherline — Lathe Operating Instructions (PDF)',
        url: 'https://www.sherline.com/wp-content/uploads/2016/04/lathe_inst.pdf',
      },
      {
        label: 'Wikipedia — Digital Readout (DRO)',
        url: 'https://en.wikipedia.org/wiki/Digital_readout',
      },
    ],
    toolsNeeded: [
      'Your lathe (power off, locked out for the walk-around)',
      'The manufacturer\'s manual for your specific machine — every lathe has minor layout differences',
      'A clean rag and a bright light',
      'Optional: phone camera to label parts as you identify them',
    ],
    preChecklist: [
      'Power off, plug pulled or breaker locked out — you are exploring, not running',
      'Spindle stationary, chuck guard down if equipped',
      'Carriage parked clear of the chuck and tailstock',
      'All wrenches, chuck keys, and rags removed from machine surfaces',
      'Bed ways wiped clean before you set anything down (or better: don\'t set anything down)',
    ],
    commonMistakes: [
      'Confusing the apron handwheel (carriage, left-right) with the cross-slide handwheel (in-out). The two motions are perpendicular and produce different cuts.',
      'Forgetting that the compound rest dial reads movement along its swiveled axis, not depth into the workpiece. At an angle, depth ≠ dial reading.',
      'Engaging the half-nut during a normal turning pass instead of the feed lever. The half-nut runs at threading speed and will destroy the cut.',
      'Trusting the DRO without confirming whether it is in diameter or radius mode. A 0.005″ "depth of cut" in radius mode is a 0.010″ change in diameter.',
      'Setting tools, drinks, or rags on the ways. The polished V-grooves are precision reference surfaces — keep them clean.',
    ],
    practiceExercises: [
      {
        id: 'name-the-castings',
        description: 'Stand at the operator\'s position. Point to and name aloud the four major castings: bed, headstock, tailstock, carriage assembly. Repeat until it is automatic.',
        hint: 'Do this without looking at any diagram. If you have to think about which is which, you have not learned the vocabulary yet.',
      },
      {
        id: 'movement-drill',
        description: 'With the spindle OFF, hand-crank the carriage from the headstock end to the tailstock end using the apron handwheel. Count the revolutions. Then move it back. Now do the same with the cross-slide — note the dial reading per revolution and confirm whether your machine reads radius or diameter on the X dial.',
        hint: 'Most modern lathes are diameter-reading on the X dial; some older machines (and most compound rests) are radius. Read your manual or test with a dial indicator if you are unsure.',
      },
      {
        id: 'dro-trust-check',
        description: 'If your machine has a DRO: with the spindle off, jog the cross-slide a known distance using the handwheel (say one full turn of the dial). Confirm the DRO moved by the expected amount, and identify whether it is in diameter or radius mode.',
        hint: 'No DRO? Skip this one — but make a note to check the cross-slide dial calibration the same way the first time you turn something to a target diameter.',
      },
    ],
    unlockAfterModule: 'measuring',
  },
  {
    id: 'lathe-workholding',
    number: 4,
    machine: 'lathe',
    title: 'Workholding — 3-Jaw Chuck Basics',
    subtitle: 'Mounting stock, runout, jaw direction, and chuck-key etiquette',
    description: 'How to mount round stock in a 3-jaw scroll chuck, check the runout you actually get, reverse the jaws when you need to, and never (ever) leave the chuck key in the chuck. The chuck is the bridge between the spindle and the cut — sloppy here, sloppy everywhere downstream.',
    estimatedTime: '15 min',
    icon: '🔧',
    sections: [
      {
        id: 'how-it-works',
        title: 'How a 3-jaw scroll chuck works',
        content: `
          <p>A 3-jaw scroll chuck centers a workpiece automatically by linking all three jaws to a single spiral plate — the <strong>scroll</strong>. Turn the chuck key in any of the three key sockets and the scroll rotates; the spiral pulls all three jaws inward (or pushes them outward) by the same amount at the same time. That is the magic: one motion grips the part. <a href="https://sherline.com/wp-content/uploads/2015/01/1040inst.pdf" target="_blank" rel="noopener">[Sherline 3-Jaw Chucks]</a></p>
          <p>The cost of that convenience is precision. The scroll is a precision-ground spiral, but wear, dirt, and the inherent geometry of "average all three jaws together" mean a 3-jaw will not center a part as tightly as a 4-jaw with individual adjustment. In good condition, expect <strong>0.001″-0.003″ TIR</strong> (total indicator runout) on a small workpiece. Older or worn chucks can be 0.005″-0.010″.</p>
          <p>This is fine — even great — for most turning work. The diameter you turn ends up concentric to the spindle axis no matter how the raw stock was mounted, because every cut you take is relative to the spinning axis. It only matters when you need to <em>preserve</em> an existing feature (re-chucking a part, gripping a finished diameter to work on the other end) — then runout becomes the limit of what you can hold.</p>
        `,
        callout: {
          type: 'warning',
          title: 'The chuck key — again',
          text: 'Shop Safety covered this. Workholding is where you actually use the key, so the rule lives here too: never let go of the chuck key while it is in the chuck. Drop it on the bench before any spindle command. No exceptions, no "just for a second."',
        },
      },
      {
        id: 'mounting',
        title: 'Mounting stock — the procedure',
        content: `
          <p>Round stock into a 3-jaw chuck, step by step:</p>
          <ol>
            <li><strong>Wipe everything.</strong> Chuck face, jaw faces, and the workpiece itself. A single chip behind a jaw face throws everything off-center.</li>
            <li><strong>Open the jaws</strong> a bit wider than your stock diameter using the chuck key. (Remember: key in your hand the whole time it is in the chuck.)</li>
            <li><strong>Insert the work</strong> with at least <strong>1× diameter</strong> of grip — for a 1″ rod that means at least 1″ of stock inside the jaws. Less grip and the part can pull out under cutting load.</li>
            <li><strong>Sticking out:</strong> keep the unsupported length as short as practical. The longer it sticks out, the more it deflects under the tool. Anything longer than 3-4× diameter wants tailstock support.</li>
            <li><strong>Tighten the chuck</strong> with the key, then snug it firmly with one hand on the key. Do not use a cheater bar — chucks are designed for hand-tight, not gorilla-tight.</li>
            <li><strong>Remove the key. Put it on the bench.</strong> Verify with your eyes that the key is out of the chuck before you go near the spindle switch.</li>
          </ol>
          <p>For long stock that extends out the back of the spindle: it must be contained. A rod whipping at 1000 RPM out the back of the headstock will sweep the shop. Use stock that fits inside the spindle bore, or use a spindle liner / steady, or shorten the stock. <a href="https://tricities.wsu.edu/wp-content/uploads/WSUTC_Lathe-Operation-2-3-2018.pdf" target="_blank" rel="noopener">[WSU Tri-Cities SOP]</a></p>
        `,
      },
      {
        id: 'inside-outside-jaws',
        title: 'Inside vs outside jaws — flipping for ID work',
        content: `
          <p>Most 3-jaw chucks come with two sets of jaws: <strong>outside jaws</strong> (the default, with stepped gripping surfaces that face inward — for clamping the OD of small-to-medium work) and <strong>inside jaws</strong> (which face outward — for gripping the ID of a bore or for clamping large-OD rings from inside). <a href="https://sherline.com/wp-content/uploads/2015/01/1040inst.pdf" target="_blank" rel="noopener">[Sherline]</a></p>
          <p>On some smaller chucks the jaws are <strong>reversible</strong> — the same jaw flips around to work either way. On larger and more common chucks the two sets are separate pieces that you swap. Either way, the install order matters:</p>
          <ul>
            <li><strong>Jaws are numbered 1, 2, 3</strong> and the chuck slots are numbered to match. Install in order — jaw 1 in slot 1, then 2, then 3 — turning the scroll between each so the scroll catches each jaw\'s tooth.</li>
            <li><strong>Wrong order = jaws don\'t close evenly.</strong> The scroll picks up each jaw at a different position on the spiral, and you get a chuck that grips three completely different diameters.</li>
            <li><strong>After swapping</strong>, run the chuck closed and re-open it a few times to confirm all three jaws move together smoothly.</li>
          </ul>
          <p>Inside jaws are how you grip a workpiece by an existing bore — for example, to face the back side of a part you already turned the outside of.</p>
        `,
        callout: {
          type: 'tip',
          title: 'The numbered keyway',
          text: 'Many chucks have one "master" keyway (sometimes marked) that gives the most repeatable centering. If your chuck has one, learn which it is and use it consistently. Switching keyways can shift runout by a few thou.',
        },
      },
      {
        id: 'runout',
        title: 'Runout — measure what you actually have',
        content: `
          <p>The only way to know your chuck\'s runout is to measure it. Setup:</p>
          <ol>
            <li>Mount a clean, straight reference (a ground dowel pin or a freshly turned diameter on the same chuck). 1″ stock works fine.</li>
            <li>Set up a dial indicator on a magnetic base, with the contact tip on the OD of the reference, about 1″ out from the jaws.</li>
            <li>Rotate the spindle by hand through one full revolution.</li>
            <li>Read the total swing — highest to lowest. That is your TIR.</li>
          </ol>
          <p>If your 3-jaw reads 0.001″-0.003″ on a 1″ reference, it is in good shape. If it reads 0.005″+ on a clean reference, check for chips between jaws and chuck, then check for worn jaws or scroll. If the chuck is well-maintained and still reads high, that is just the chuck — plan around it.</p>
          <p>If you need lower runout for a specific part, your options are: (a) turn the diameter you need from the same chuck mounting (no re-chucking, no runout), (b) move to a 4-jaw chuck and dial it in with an indicator, or (c) use collets.</p>
        `,
      },
      {
        id: 'when-to-step-up',
        title: 'When to step up to a 4-jaw',
        content: `
          <p>A 4-jaw <em>independent</em> chuck has four jaws that each move separately — there is no scroll, just four individual screws. You center the work yourself, using a dial indicator. Done with patience, a 4-jaw can hold a part to <strong>0.0001″-0.001″ TIR</strong> — an order of magnitude better than a 3-jaw.</p>
          <p>When the extra time is worth it:</p>
          <ul>
            <li>Re-chucking a finished part to work on a second feature, and the two features must be coaxial.</li>
            <li>Gripping a part by an already-machined diameter where small runout would show in the finished surface.</li>
            <li>Holding non-round stock — square bar, hex, or irregular castings. A 3-jaw cannot do this; a 4-jaw shrugs.</li>
            <li>Any time the part-to-part feature concentricity matters more than setup speed.</li>
          </ul>
          <p>For everything else — roughing out a piece of round bar stock, making a part from scratch where every diameter is cut in the same setup — the 3-jaw is the right tool.</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue',
          text: 'Mount, indicate, write the number down. Every chuck on every machine has a runout signature — learn yours. If your 3-jaw normally reads 0.0015″ and today it reads 0.005″, you have a chip behind a jaw or a worn surface. The "normal" number is only useful once you know it.',
        },
      },
    ],
    quiz: [
      {
        id: 'typical-runout',
        type: 'multiple-choice',
        question: 'Typical total indicator runout (TIR) for a 3-jaw scroll chuck in good condition on a 1″ reference is:',
        options: [
          '0.0001″ - 0.0005″',
          '0.001″ - 0.003″',
          '0.010″ - 0.020″',
          'Zero — that\'s the point of a scroll chuck',
        ],
        correctIndex: 1,
        explanation: '0.001″-0.003″ TIR is the realistic working range. Anything tighter requires a 4-jaw and an indicator; anything looser usually means dirt, a chip behind a jaw, or wear.',
      },
      {
        id: 'jaw-install-order',
        type: 'true-false',
        question: 'When swapping chuck jaws, the numbered jaws must go into matching numbered slots in order (1, then 2, then 3), turning the scroll between each.',
        options: ['True', 'False'],
        correctIndex: 0,
        explanation: 'True. The scroll picks up each jaw\'s tooth at a different angular position. Install out of order and the three jaws will close at three different diameters — useless.',
      },
      {
        id: 'grip-length',
        type: 'multiple-choice',
        question: 'A reasonable minimum amount of stock to have inside the jaws of a 3-jaw chuck is:',
        options: [
          'Half the stock diameter',
          'About 1× the stock diameter',
          'About 3× the stock diameter',
          'As little as possible to maximize working length',
        ],
        correctIndex: 1,
        explanation: 'A rough rule of thumb is at least 1× diameter of grip. Less and the part can pull out under cutting load. If you need more working length than that allows, use tailstock support.',
      },
      {
        id: 'when-4-jaw',
        type: 'multiple-choice',
        question: 'You\'re re-chucking a finished part to work on its other end. The two faces must be coaxial within 0.0005″. The right workholding choice is:',
        options: [
          'The same 3-jaw chuck used originally',
          'The 3-jaw with the inside jaws installed',
          'A 4-jaw independent chuck dialed in with an indicator',
          'The tailstock alone',
        ],
        correctIndex: 2,
        explanation: '0.0005″ is tighter than a 3-jaw\'s working runout. A 4-jaw lets you adjust each jaw individually against an indicator until you have the runout you need. Collets are another good option if you have them.',
      },
      {
        id: 'long-stock',
        type: 'multiple-choice',
        question: 'You have a 36″ length of 1/2″ round stock and you only need 4″ for the part. The safe approach is:',
        options: [
          'Stick all 36″ out the back of the headstock and grip the front',
          'Cut the stock down so nothing protrudes from the back of the spindle',
          'Grip in the middle and have 18″ sticking out the front',
          'Use a chuck key for extra grip on the long end',
        ],
        correctIndex: 1,
        explanation: 'Stock whipping out the back of the spindle is one of the more dangerous lathe scenarios — at 1000+ RPM it will sweep the shop. Either cut it short or contain it inside the spindle bore with a tube or spindle liner.',
      },
    ],
    videos: [
      {
        title: 'Metal Lathe Tutorial 4: Chucks and Workholding',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks metal lathe tutorial chucks workholding',
        description: 'Quinn covers the 3-jaw, jaw reversal, and the indicator-checked chuck install. Clear visuals on every step.',
      },
      {
        title: 'How to Use a Lathe Chuck — 3-Jaw Setup',
        channel: 'mrpete222 / Tubalcain',
        searchQuery: 'Tubalcain mrpete222 3 jaw chuck lathe setup',
        description: 'Tubalcain\'s shop-class take on chuck use. Watch his hands — the chuck-key habit is exactly what to copy.',
      },
    ],
    sources: [
      {
        label: 'Sherline — 3-Jaw Chucks Instructions (PDF)',
        url: 'https://sherline.com/wp-content/uploads/2015/01/1040inst.pdf',
      },
      {
        label: 'Sherline — 4-Jaw Self-Centering Chucks Instructions (PDF)',
        url: 'https://sherline.com/wp-content/uploads/2015/01/1075inst.pdf',
      },
      {
        label: 'WSU Tri-Cities — Manual Toolroom Lathes SOP (PDF)',
        url: 'https://tricities.wsu.edu/wp-content/uploads/WSUTC_Lathe-Operation-2-3-2018.pdf',
      },
      {
        label: 'Oregon State ATAMI — Lathe Manual and Operating Procedures (PDF)',
        url: 'https://atami.oregonstate.edu/sites/atami.oregonstate.edu/files/atami_lathe_manual_and_operating_procedures.pdf',
      },
      {
        label: 'U.S. Army TC 9-524 Ch. 7 — Lathes (PDF, hosted by CMU)',
        url: 'https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf',
      },
    ],
    toolsNeeded: [
      'The 3-jaw chuck that came with your lathe (already mounted)',
      'The chuck key that fits your chuck — they are not interchangeable across machines',
      'A dial indicator with magnetic base (from the Measuring module) for runout checks',
      'A clean rag and a small brush for clearing the chuck face',
      'A piece of clean round stock for practice — 1/2″ or 3/4″ ground stock is ideal',
      'Optional: a second set of jaws (inside / outside) if your chuck supports swapping',
    ],
    preChecklist: [
      'Power off, locked out — you are setting up, not cutting',
      'Chuck face wiped clean — no chips, no coolant residue between jaws and scroll',
      'Chuck key in your hand, never in the chuck when unattended',
      'Workpiece cleaned and deburred — burrs cock the part off-center',
      'Indicator and magnetic base ready if you plan to check runout',
      'You know which keyway is your "master" (if your chuck has one)',
    ],
    commonMistakes: [
      'Gripping too little length of stock — less than 1× diameter and the part can pull out under cutting load.',
      'Leaving long stock protruding out the back of the spindle — it will whip dangerously at speed.',
      'Forgetting to wipe the jaw faces and chuck pocket. A single chip throws runout off by 0.005″ or more.',
      'Installing swapped jaws out of order. They must go 1-2-3 in matching slots with the scroll turned between each.',
      'Tightening with a cheater bar. Chucks are designed for hand-tight — adding leverage warps the scroll and damages the work.',
      'Trusting 3-jaw "self-centering" for tight tolerances. Self-centering means approximately — verify with an indicator if it matters.',
    ],
    practiceExercises: [
      {
        id: 'mount-and-indicate',
        description: 'Mount a clean piece of round stock with about 1× diameter of grip. Set up a dial indicator on a magnetic base touching the OD about 1″ out from the jaws. Rotate the spindle by hand through one full turn and record the TIR. This is your chuck\'s "normal" — write it down for future reference.',
        hint: 'Use a ground reference (dowel pin or freshly-turned diameter) for the most honest reading. Anything else might be measuring the stock\'s own variation instead of the chuck.',
      },
      {
        id: 'compare-keyways',
        description: 'Without changing anything else, take the same workpiece out and re-mount it using a different chuck-key socket. Measure runout again. Repeat for the third socket. Note any differences.',
        hint: 'Many chucks have a "best" keyway that produces lower runout. If yours does, that is the one to use every time.',
      },
      {
        id: 'jaw-swap-drill',
        description: 'If your chuck supports swapped jaws (inside / outside): remove the outside jaws (one at a time, noting which slot each came from), then install the inside set in order 1-2-3, turning the scroll between each. Close the chuck on a piece of ID work or a large ring and check it grips concentrically.',
        hint: 'If the inside jaws do not close evenly, you installed them out of order. Pull them, restart at jaw 1.',
      },
    ],
    unlockAfterModule: 'lathe-anatomy',
  },
  {
    id: 'lathe-cutting-tools',
    number: 5,
    machine: 'lathe',
    title: 'Cutting Tools 101 (Lathe)',
    subtitle: 'Right-hand turning, facing, parting, boring — geometry and use',
    description: 'Recognize the common lathe tool geometries, pick the right one for each operation, and set it up so it can actually do its job. A cut is only as good as the tool that makes it — and "the right tool" means right shape, right material, right height, and right overhang.',
    estimatedTime: '20 min',
    icon: '🪛',
    sections: [
      {
        id: 'hss-vs-carbide',
        title: 'HSS vs carbide — pick your weapon',
        content: `
          <p>Lathe cutting tools come in two main families:</p>
          <ul>
            <li><strong>HSS (high-speed steel).</strong> A solid bar of tool steel that you grind to whatever shape you need. Forgiving — survives a light bump, can be reground, cuts at lower surface speeds. The learning curve favors HSS because you can fix a chipped edge at the bench grinder. Standard starter shape is the 1/4″ or 5/16″ square blank.</li>
            <li><strong>Carbide insert.</strong> A replaceable tip clamped into a steel holder. Harder, faster (runs at 2-3× the surface speed of HSS), holds its edge longer — but brittle. A bump can shatter the edge. Standard hobby beginner inserts are CCMT-style (positive rake, gentle on small machines) in 21.51 or similar small sizes.</li>
          </ul>
          <p>Most beginner machinists start with HSS, switch to carbide inserts as they get comfortable, and end up keeping both in the drawer. HSS for unusual profiles, undersized features, and anything that requires improvisation; inserts for production-style turning where you want consistent finish and don\'t want to grind a tool. <a href="https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf" target="_blank" rel="noopener">[Army TC 9-524 Ch. 7]</a></p>
        `,
        callout: {
          type: 'warning',
          title: 'Carbide is glass',
          text: 'Carbide inserts will shatter if you drop them, bump them on a chuck, or run them into hardened material at the wrong speed. They are not "tougher" than HSS — they are harder. Hardness and toughness are opposites. Treat carbide gently and it lasts forever; treat it like HSS and it fails in an instant.',
        },
      },
      {
        id: 'geometry',
        title: 'Tool geometry — the angles that actually matter',
        content: `
          <p>Every lathe tool is a wedge with carefully chosen angles. The names sound technical, but the function of each is straightforward:</p>
          <ul>
            <li><strong>Rake angle</strong> (back rake and side rake) is the tilt of the top face. Positive rake (top sloping down and away from the cutting edge) gives a freer, lighter cut — easier on small machines. Negative rake gives a stronger edge for heavier cuts and harder materials.</li>
            <li><strong>Relief angle</strong> (end relief and side relief) is the clearance beneath the cutting edge so the tool body does not drag against the work. Too little relief and the tool rubs instead of cutting. Too much and the edge is fragile.</li>
            <li><strong>Nose radius</strong> is the curve at the very tip. A larger radius gives a better surface finish (the cut feeds blend into each other) but increases cutting force. Small radius cuts cleanly but leaves a rougher finish unless you feed slowly.</li>
          </ul>
          <p>You do not need to memorize specific angle values. You need to recognize the parts of the tool, understand what each angle does, and know that if a tool is cutting badly the answer is usually <em>geometry</em>: wrong angle for the material, dull edge, or insufficient relief.</p>
        `,
      },
      {
        id: 'common-shapes',
        title: 'The common shapes — what each tool does',
        content: `
          <p>For starter work you need four basic tool shapes. Each does one job well:</p>
          <ul>
            <li><strong>Right-hand turning tool.</strong> Cutting edge on the left side of the tool tip, designed to feed from the tailstock toward the headstock (right to left). Turns down an OD. Most common everyday tool.</li>
            <li><strong>Facing tool.</strong> Cutting edge angled so the tool feeds inward (cross-slide motion) across the end of the work. Produces a flat, square end. Many right-hand turning tools can face if the lead angle works; dedicated facing tools have a steeper approach for clean cuts to the center.</li>
            <li><strong>Parting (cutoff) tool.</strong> Thin, deep blade. Plunges straight in from the cross-slide to cut a piece off. Tricky — narrow tool, lots of friction, chatter-prone. Run at lower speed than turning and feed steadily.</li>
            <li><strong>Boring bar.</strong> Long, thin tool that reaches inside a bore (existing hole) and enlarges it. Stiffness is the limit — the longer the bar, the more it deflects. Use the shortest bar that reaches.</li>
          </ul>
          <p>Other common tools you will accumulate: <em>threading tool</em> (60° vee for standard threads), <em>knurling tool</em> (pressing rollers, not cutting), <em>left-hand turning tool</em> (mirror of right-hand for when geometry demands it), and a <em>chamfer tool</em> (45° for breaking edges).</p>
        `,
        callout: {
          type: 'tip',
          title: 'Pick the tool, then the cut',
          text: 'New machinists pick a depth of cut and feed, then ask the tool to handle it. The pros do it backwards: pick the tool that fits the job, then run the cut the tool wants. If the tool chatters, deflects, or burns, the wrong tool is in the holder.',
        },
      },
      {
        id: 'tool-height',
        title: 'Tool height — on center, every time',
        content: `
          <p>The single most important geometric setup on a lathe is getting the tool tip exactly on the spindle centerline. The reason is that every rake and relief angle on your tool was ground assuming the cutting edge meets the work on a horizontal line through the spindle axis. Move off that line and you change the effective angles. <a href="https://www.mmsonline.com/articles/mind-the-tool-center-height-when-turning-small-diameters" target="_blank" rel="noopener">[Modern Machine Shop — center height on small diameters]</a></p>
          <ul>
            <li><strong>Tool too high:</strong> effective rake increases, relief decreases. The tool digs in — depth of cut runs deeper than commanded. Surface finish worsens, and on a parting tool it can grab and snap.</li>
            <li><strong>Tool too low:</strong> effective rake decreases, relief increases. The tool rubs more than cuts. On OD turning the cut goes shallower than commanded; on a parting tool you leave a pip in the center.</li>
            <li><strong>The cost of being off scales with diameter.</strong> On a 1″ shaft, 0.005″ off-center barely matters. On a 0.060″ pin it dominates everything.</li>
          </ul>
          <p>Industry recommendation (Sandvik): tool tip within 0.004″ of centerline as a working tolerance. For OD turning, on-center or very slightly below is safe; never above. <strong>How to set it:</strong></p>
          <ol>
            <li><strong>Tailstock-center method:</strong> bring a live or dead center into the tailstock quill. Slide a straight rule between the tool tip and the center point. If the rule stays vertical, the tool is on center. If it tips toward you, the tool is high; tips away, low.</li>
            <li><strong>Rule-against-the-work method:</strong> hold a 6″ rule vertically between the tool tip and the OD of a piece of round stock in the chuck. Same logic — rule vertical means tool on center.</li>
            <li><strong>Face a test piece:</strong> face the end of a piece of stock with the tool you just set. If a tiny pip is left in the center, the tool is below center. If the cut deepens and runs out smoothly, the tool is on or just below center. Adjust and try again.</li>
          </ol>
        `,
      },
      {
        id: 'overhang-and-good',
        title: 'Overhang, holder rigidity, and what good looks like',
        content: `
          <p>Even a perfect tool fails if it is mounted poorly. Two rules:</p>
          <ul>
            <li><strong>Minimize overhang from the holder.</strong> Anything past the holder face flexes under cutting force. Rule of thumb: tool stick-out from the holder should be no more than 1.5× the tool\'s height (for a 1/2″ tall tool, no more than ~3/4″ of overhang).</li>
            <li><strong>Boring bars get a stricter rule:</strong> 4× diameter overhang max for steel bars; 6-8× for solid carbide bars. Beyond that, the bar deflects and you get chatter, taper, and bad finish.</li>
          </ul>
          <p>The tool post itself matters. A loose tool post or a tool not seated flat in its holder is just a softer tool. Snug everything by hand, check that the tool is bottomed in the holder slot, and that the tool post is locked tight against its base.</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue',
          text: 'A correctly chosen, correctly set tool produces a thin, shiny ribbon chip on mild steel, a clean continuous spiral on aluminum, and a smooth surface that does not need a finishing pass to make presentable. Torn metal, dull color on the chip, or a finish that looks ground rather than turned are the tool telling you something is wrong — usually geometry, height, or overhang.',
        },
      },
    ],
    quiz: [
      {
        id: 'rake-purpose',
        type: 'multiple-choice',
        question: 'A positive rake angle on a lathe tool generally:',
        options: [
          'Makes the edge stronger for heavy cuts',
          'Produces a freer, lighter cut at the cost of edge strength',
          'Is required for parting tools',
          'Eliminates the need to set tool height on center',
        ],
        correctIndex: 1,
        explanation: 'Positive rake (top of the tool sloping down and away from the cutting edge) reduces cutting force and gives a freer cut. Great for small machines and softer materials. The trade-off is a less robust edge that can chip under heavy cuts or in hard material.',
      },
      {
        id: 'tool-height-od',
        type: 'true-false',
        question: 'For outside-diameter turning, the tool should be set on center or very slightly below — never above.',
        options: ['True', 'False'],
        correctIndex: 0,
        explanation: 'True. A tool above center on the OD increases effective rake and reduces relief — the tool digs in, depth of cut runs deeper than commanded, and the finish suffers. On-center is ideal; slightly low is safe; above is dangerous, especially for parting.',
      },
      {
        id: 'right-tool-for-job',
        type: 'multiple-choice',
        question: 'You need to take 0.060″ off the face of a 2″ diameter workpiece. The right tool is:',
        options: [
          'A right-hand turning tool fed parallel to the spindle',
          'A facing tool fed inward across the face from OD to center',
          'A parting tool plunged into the face',
          'A boring bar reached in from the side',
        ],
        correctIndex: 1,
        explanation: 'Facing a workpiece means feeding the tool across the end (cross-slide motion, OD to center) to produce a flat square end. A turning tool can sometimes face, but a dedicated facing tool has the right approach angle to clean up cleanly to the center.',
      },
      {
        id: 'boring-overhang',
        type: 'multiple-choice',
        question: 'A boring bar should generally not stick out from its holder by more than:',
        options: [
          '1.5× the bar height',
          '4× the bar diameter (for steel bars)',
          'The depth of the bore — whatever it takes',
          'There is no limit if the bar is held tight',
        ],
        correctIndex: 1,
        explanation: 'Boring bars deflect dramatically with overhang. Steel bars: ~4× diameter max. Solid carbide bars: 6-8× diameter. Beyond that you get chatter, deflection, and tapered bores. Always use the shortest bar that reaches.',
      },
      {
        id: 'finish-diagnosis',
        type: 'multiple-choice',
        question: 'You take a turning cut on mild steel and the surface comes out torn and rough rather than shiny. The first three things to check are:',
        options: [
          'Increase RPM, increase feed, take a deeper cut',
          'Tool sharpness, tool height (on center?), tool geometry for the material',
          'Spindle bearings, lubrication, room temperature',
          'Switch to a parting tool — finish doesn\'t matter on roughing',
        ],
        correctIndex: 1,
        explanation: 'A torn finish almost always means the tool is wrong — dull, off-center, or wrong geometry. Speed and feed adjustments come after you have a sharp, correctly-set tool. The tool itself is the most common variable.',
      },
    ],
    videos: [
      {
        title: 'Metal Lathe Tutorial: Grinding HSS Tool Bits',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks grinding HSS tool bits lathe',
        description: 'Quinn walks through grinding a basic right-hand turning tool from a blank — the foundational skill for understanding tool geometry.',
      },
      {
        title: 'Lathe Tool Height — Why It Matters',
        channel: 'Joe Pieczynski',
        searchQuery: 'Joe Pieczynski lathe tool height center',
        description: 'Joe Pie shows what happens at the cut when the tool is above, on, and below center — the geometry lesson made concrete.',
      },
    ],
    sources: [
      {
        label: 'U.S. Army TC 9-524 Ch. 7 — Lathes (PDF, hosted by CMU)',
        url: 'https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf',
      },
      {
        label: 'Modern Machine Shop — Mind the Tool Center Height When Turning Small Diameters',
        url: 'https://www.mmsonline.com/articles/mind-the-tool-center-height-when-turning-small-diameters',
      },
      {
        label: 'CNC Cookbook — Tool Centerline Tolerances for Turning',
        url: 'https://www.cnccookbook.com/tool-centerline-tolerances-for-turning/',
      },
      {
        label: 'Open Oregon Manufacturing Processes 4-5 — Speed and Feed (background on tool selection)',
        url: 'https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-2-speed-and-feed/',
      },
      {
        label: 'WSU Tri-Cities — Manual Toolroom Lathes SOP (PDF)',
        url: 'https://tricities.wsu.edu/wp-content/uploads/WSUTC_Lathe-Operation-2-3-2018.pdf',
      },
    ],
    toolsNeeded: [
      'A tool holder mounted on your lathe (QCTP, AXA-style, or lantern — whatever your machine has)',
      'A starter set of tools: right-hand turning, facing, parting, boring bar (HSS or carbide insert — either is fine for learning)',
      'A 6″ steel rule for tool-height setting',
      'A tailstock center (live or dead) for the tailstock-center height method',
      'Allen keys / wrenches for your tool holder and insert clamps',
      'Optional: a tool-height gauge if you have one, or a piece of test stock to face',
    ],
    preChecklist: [
      'Tool clean, free of chips, secure in its holder',
      'Tool tip set on center (or within 0.004″) — verified by rule or test cut',
      'Tool overhang from holder no more than 1.5× tool height (or 4× diameter for steel boring bars)',
      'Correct tool for the planned operation (turning vs facing vs parting vs boring)',
      'Tool edge sharp / insert face clean and not chipped',
      'Tool post locked tight against the cross-slide',
    ],
    commonMistakes: [
      'Tool set above center on OD turning — causes dig-ins, bad finish, broken parting tools.',
      'Excessive tool overhang from the holder — flex, chatter, and a tapered cut you didn\'t ask for.',
      'Wrong tool for the operation. A right-hand turning tool faces poorly toward the chuck; a parting tool has no business taking a depth-of-cut step.',
      'Running carbide at HSS speeds (too slow) — the insert glazes and dulls. Or HSS at carbide speeds (too fast) — the edge burns off.',
      'Dropping or banging carbide inserts. Even a small bench impact can micro-fracture the edge so the next cut tears instead of slices.',
      'Storing HSS tool bits loose in a drawer where they bang together. Chipped edges = unusable tools.',
    ],
    practiceExercises: [
      {
        id: 'tool-height-set',
        description: 'Set your most-used turning tool on center using the tailstock-center method (rule between tool tip and tailstock center point — should stand vertical). Then verify by facing a test piece of stock: if a pip is left in the center, the tool is low. Adjust and re-face until the pip disappears.',
        hint: 'The pip-on-face test is the most honest height check you have. The rule method gets you close; the face cut tells the truth.',
      },
      {
        id: 'identify-tools',
        description: 'Lay out your starter tool collection on the bench. Name each one out loud: right-hand turning, facing, parting, boring bar, threading, etc. If you cannot name it, set it aside and look it up before using it.',
        hint: 'A tool with no name is a tool with no purpose. If you do not know what it does, you will misuse it.',
      },
      {
        id: 'overhang-experiment',
        description: 'Mount a turning tool with about 0.5″ of overhang, take a light cut on a piece of stock, and listen / look at the finish. Now extend the tool to ~2× the original overhang and take the same cut. Compare the finish, the sound, and any chatter marks. The difference is what overhang costs you.',
        hint: 'You may hear chatter (high-pitch buzz) on the longer overhang even at light depth. Once you have heard it, you will recognize it in real cuts.',
      },
    ],
    unlockAfterModule: 'lathe-workholding',
  },
  {
    id: 'lathe-speeds-feeds',
    number: 6,
    machine: 'lathe',
    title: 'Speeds & Feeds (Lathe)',
    subtitle: 'SFM, RPM, feed-per-rev, and rules of thumb that keep you out of trouble',
    description: 'Compute spindle speed from surface footage, pick a feed rate that produces a clean chip, and know when to deviate from the chart. The chart is the starting point — the chip and the sound of the cut are the answer.',
    estimatedTime: '25 min',
    icon: '📊',
    sections: [
      {
        id: 'sfm-concept',
        title: 'What surface footage actually means',
        content: `
          <p><strong>Surface footage (SFM, or "surface feet per minute")</strong> is the speed at which the cutting edge travels across the workpiece, measured in feet per minute. It is not spindle RPM — it is how fast the surface of the work is moving past the tool.</p>
          <p>Every cutting tool material is designed to work best at a particular surface speed range. Run an HSS tool too fast and the cutting edge gets hot enough to soften — within minutes, the tool is dead. Run carbide too slow and the insert glazes (the chip rubs instead of cutting), the work-hardens, and surface finish degrades. <a href="https://en.wikipedia.org/wiki/Surface_feet_per_minute" target="_blank" rel="noopener">[Wikipedia — SFPM]</a></p>
          <p>The key insight: <strong>SFM depends on both spindle RPM and workpiece diameter.</strong> A 2″ shaft turning at 200 RPM has the same surface speed as a 1″ shaft turning at 400 RPM (both ~105 SFM). The same RPM produces different SFM as you change diameters — which means as you cut a part smaller, the SFM drops and you may need to crank RPM up to compensate.</p>
        `,
        callout: {
          type: 'warning',
          title: 'Speed kills the tool, not the part',
          text: 'The single most common new-machinist mistake is running RPM too high — usually because the spindle is quiet at low RPM and "feels too slow." A burned HSS tool, a glazed carbide insert, or a torn surface finish is the lathe telling you the SFM is wrong. Calculate before you cut.',
        },
      },
      {
        id: 'rpm-formula',
        title: 'From SFM to RPM — the formula',
        content: `
          <p>The exact relationship:</p>
          <p style="text-align: center; font-size: 1.1rem;"><strong>RPM = (SFM × 12) / (π × D)</strong></p>
          <p>where D is workpiece diameter in inches. The 12 converts feet to inches; π converts diameter to circumference.</p>
          <p>For shop math, the practical shortcut everyone uses:</p>
          <p style="text-align: center; font-size: 1.1rem;"><strong>RPM ≈ (SFM × 4) / D</strong></p>
          <p>The 4 is just 12/π rounded (≈ 3.82). Close enough — the underlying SFM values are starting points anyway. <a href="https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-2-speed-and-feed/" target="_blank" rel="noopener">[Open Oregon Mfg Processes 4-5]</a></p>
          <p><strong>Worked example:</strong> Turning 1″ mild steel with an HSS tool. Recommended SFM for mild steel with HSS is around 100. So:</p>
          <p style="text-align: center;">RPM = (100 × 4) / 1.0 = <strong>400 RPM</strong></p>
          <p>Set the lathe to the closest available speed (probably 350 or 450 on a geared-head machine; exact on a VFD).</p>
          <p><strong>Second example:</strong> Same setup, now turning a 0.5″ diameter section. Same 100 SFM target:</p>
          <p style="text-align: center;">RPM = (100 × 4) / 0.5 = <strong>800 RPM</strong></p>
          <p>The smaller you cut, the faster you need to spin to maintain SFM. Many machinists set RPM for the starting diameter and forget — by the time they are down to half size, they are cutting at half the intended SFM. On long parts with big diameter swings, bump the RPM up as the part shrinks.</p>
        `,
      },
      {
        id: 'sfm-table',
        title: 'Picking SFM for your material',
        content: `
          <p>Recommended starting SFM values for turning common materials. These are conservative — meant to get you cutting without burning a tool. Once you have heard and seen what works on your machine, you can deviate confidently.</p>
          <p><strong>HSS tooling:</strong></p>
          <ul>
            <li><strong>Mild steel (1018, 1020):</strong> 80-120 SFM</li>
            <li><strong>Free-machining steel (12L14):</strong> 150-200 SFM</li>
            <li><strong>4140 alloy steel:</strong> 60-90 SFM</li>
            <li><strong>Aluminum (6061, 7075):</strong> 200-400 SFM</li>
            <li><strong>Brass (free-machining):</strong> 200-300 SFM</li>
            <li><strong>Cast iron (gray):</strong> 60-100 SFM</li>
            <li><strong>Stainless 303 (annealed):</strong> 100-120 SFM <a href="https://en.wikipedia.org/wiki/Surface_feet_per_minute" target="_blank" rel="noopener">[Wikipedia]</a></li>
            <li><strong>Stainless 304:</strong> 60-90 SFM (work-hardens easily — run lower than 303)</li>
          </ul>
          <p><strong>Carbide tooling:</strong> roughly 2-3× the HSS values above. Mild steel with carbide: ~250-400 SFM. Aluminum with carbide: up to 1000+ SFM on a stiff enough machine.</p>
          <p>These are <em>starting</em> values from the chart. The Machinery\'s Handbook has more comprehensive tables, but for hobby work the values above cover 90% of what you will encounter. <a href="https://www.cnccookbook.com/what-is-sfm-plus-sfm-to-rpm/" target="_blank" rel="noopener">[CNC Cookbook — SFM]</a></p>
        `,
        callout: {
          type: 'tip',
          title: 'When in doubt, slower',
          text: 'A cut that is too slow finishes the part eventually. A cut that is too fast destroys the tool — and sometimes the part — before you can react. For your first cut on an unfamiliar material, start at the low end of the SFM range and bump up only if the chip looks tired.',
        },
      },
      {
        id: 'feed',
        title: 'Feed per revolution — the second dial',
        content: `
          <p>Lathe feed is specified in <strong>inches per revolution (IPR)</strong> — how far the tool advances along the work for each rotation of the spindle. It is set by the feed gear lever on the apron, and the actual feed-per-minute is just IPR × RPM.</p>
          <p>Starting values for turning mild steel with HSS:</p>
          <ul>
            <li><strong>Roughing cuts:</strong> 0.005 - 0.020 IPR. Heavier feed removes material faster, but produces a rougher surface and stresses the tool more.</li>
            <li><strong>Finishing cuts:</strong> 0.001 - 0.005 IPR. Lighter feed gives a better surface and lets the tool\'s nose radius blend the cut into a smooth surface.</li>
          </ul>
          <p>The math behind surface finish: <strong>Ra ≈ feed² / (31.2 × nose radius)</strong> (in inches, approximate). Halve the feed and you quarter the surface roughness. Doubling the nose radius cuts roughness in half. This is why finishing passes use slower feeds with larger-nose tools.</p>
          <p>Depth of cut (DOC) is the third variable. For a starter machine and HSS tooling on mild steel, treat anything over 0.050″ DOC as a heavy cut and reduce feed to compensate. Carbide on a rigid machine can take much heavier cuts.</p>
        `,
      },
      {
        id: 'listening',
        title: 'Depth of cut and listening to the machine',
        content: `
          <p>The chart values are starting points. The machine and the chip are the answer.</p>
          <ul>
            <li><strong>Listen to the cut.</strong> A clean cut on steel makes a steady, low hiss. Chatter is a high-pitched buzz — back off speed, feed, or depth (try one variable at a time). Squealing is the cut binding — stop immediately.</li>
            <li><strong>Look at the chip.</strong> Mild steel at the right speed produces a thin shiny ribbon, sometimes curling into tight coils. Blue or purple chips mean too much heat (RPM too high, feed too low, or both). Long stringy chips that wrap the tool mean the chip is not breaking — use a chip breaker, increase feed, or pause to clear.</li>
            <li><strong>Watch the surface.</strong> A clean finish is shiny and uniform. A finish that looks ground or "scaly" means the tool is dull, the feed is too high for the nose radius, or the geometry is wrong for the material.</li>
            <li><strong>Feel the spindle load.</strong> If your machine has a load meter, watch it. If not, listen for the spindle pitch dropping under load — that\'s the motor working. If it bogs down, lighten the cut.</li>
          </ul>
          <p>The chart got you running; the senses keep you running well. Trust the senses over the chart when they disagree.</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue',
          text: 'The right speed and feed produce a chip that is one continuous curl, the right color for the material (silver-shiny on aluminum, light straw to medium blue on steel, never purple or black), and a finish that does not need a second pass to look presentable. Anything else is the machine telling you to adjust.',
        },
      },
    ],
    quiz: [
      {
        id: 'rpm-formula-q',
        type: 'multiple-choice',
        question: 'You want to turn 2″ aluminum at 300 SFM with a carbide insert. The approximate spindle RPM is:',
        options: [
          '150 RPM',
          '300 RPM',
          '600 RPM',
          '1200 RPM',
        ],
        correctIndex: 2,
        explanation: 'RPM ≈ (SFM × 4) / D = (300 × 4) / 2 = 600 RPM. The "× 4" shortcut is 12/π rounded — close enough for shop math.',
      },
      {
        id: 'diameter-shrinks',
        type: 'multiple-choice',
        question: 'You set RPM at 400 for a 2″ shaft at ~200 SFM. As you turn the diameter down to 1″, what happens to surface speed if you don\'t change RPM?',
        options: [
          'SFM stays at 200',
          'SFM doubles to 400',
          'SFM halves to 100',
          'SFM is unrelated to diameter',
        ],
        correctIndex: 2,
        explanation: 'Surface speed scales linearly with diameter. Half the diameter at the same RPM = half the SFM. As parts shrink during a cut, bump RPM up to maintain the intended surface speed.',
      },
      {
        id: 'carbide-vs-hss-speed',
        type: 'true-false',
        question: 'Carbide tooling can typically run at 2-3× the surface speed of HSS on the same material.',
        options: ['True', 'False'],
        correctIndex: 0,
        explanation: 'True. Carbide tolerates the higher cutting temperatures that come with higher SFM. Running carbide at HSS speeds glazes the insert and work-hardens the material; running HSS at carbide speeds burns the edge off in minutes.',
      },
      {
        id: 'finish-feed',
        type: 'multiple-choice',
        question: 'Surface finish improves most when you:',
        options: [
          'Increase feed and decrease nose radius',
          'Decrease feed and increase nose radius',
          'Increase RPM and ignore feed',
          'Use a sharper tool with a smaller nose',
        ],
        correctIndex: 1,
        explanation: 'From the surface-roughness formula Ra ≈ feed² / (31.2 × nose radius): halving the feed quarters the roughness; doubling the nose radius halves it. Finishing passes use light feeds with larger-nose tools.',
      },
      {
        id: 'mild-steel-hss',
        type: 'multiple-choice',
        question: 'A reasonable starting SFM for turning mild steel (1018) with an HSS tool is:',
        options: [
          '20-30 SFM',
          '80-120 SFM',
          '300-400 SFM',
          '800-1000 SFM',
        ],
        correctIndex: 1,
        explanation: '80-120 SFM is the standard textbook range for HSS on mild steel. Carbide can run 2-3× that. Aluminum and brass run much higher; hard alloys and stainless run lower.',
      },
    ],
    videos: [
      {
        title: 'Speeds and Feeds Demystified',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks speeds and feeds lathe',
        description: 'Quinn walks through the SFM-to-RPM conversion with worked examples on her lathe. The right starting point if the math feels abstract.',
      },
      {
        title: 'Reading the Chip — What Your Cut Is Telling You',
        channel: 'Joe Pieczynski',
        searchQuery: 'Joe Pieczynski reading chip color speeds feeds',
        description: 'Joe Pie\'s chip-reading videos pair the textbook numbers with what those numbers look like at the cut. The senses-over-chart lesson made visible.',
      },
    ],
    sources: [
      {
        label: 'Open Oregon Manufacturing Processes 4-5 — Unit 2: Speed and Feed',
        url: 'https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-2-speed-and-feed/',
      },
      {
        label: 'Wikipedia — Surface Feet Per Minute',
        url: 'https://en.wikipedia.org/wiki/Surface_feet_per_minute',
      },
      {
        label: 'CNC Cookbook — SFM to RPM Calculator and Explainer',
        url: 'https://www.cnccookbook.com/what-is-sfm-plus-sfm-to-rpm/',
      },
      {
        label: 'U.S. Army TC 9-524 Ch. 7 — Lathes (PDF, hosted by CMU)',
        url: 'https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf',
      },
      {
        label: 'WSU Tri-Cities — Manual Toolroom Lathes SOP (PDF)',
        url: 'https://tricities.wsu.edu/wp-content/uploads/WSUTC_Lathe-Operation-2-3-2018.pdf',
      },
    ],
    toolsNeeded: [
      'Your lathe with a working speed selector (gear levers or variable-speed dial)',
      'A calculator or smartphone for the RPM math',
      'A speeds-and-feeds chart for the materials you actually use (printed and kept at the machine is great)',
      'A piece of stock to practice on (mild steel, aluminum, and brass are good starter materials)',
      'Your cutting tools from the previous module (HSS or carbide insert, sharp and on-center)',
      'Optional: a machinist\'s speed/feed slide chart for quick lookups without a calculator',
    ],
    preChecklist: [
      'Material identified — you cannot pick SFM for "metal," only for a specific alloy',
      'Tool material identified (HSS or carbide) — different SFM ranges',
      'Starting workpiece diameter measured — you need a number for the RPM formula',
      'Calculated target RPM written down before changing speed',
      'Feed selection made: roughing (0.005-0.020 IPR) or finishing (0.001-0.005 IPR)',
      'Coolant or cutting fluid ready if the material wants it (steel and stainless usually do; aluminum and brass usually do not)',
    ],
    commonMistakes: [
      'Running HSS at carbide speeds — burns the cutting edge off in minutes.',
      'Running carbide at HSS speeds — glazes the insert, work-hardens the material, ruins finish.',
      'Setting RPM for the starting diameter and forgetting. As the part shrinks, SFM drops; you may end the cut at half the intended SFM.',
      'Cranking feed too high on a finishing pass. Ra scales with feed-squared — small feed reductions produce large finish improvements.',
      'Treating chart values as gospel. The chart is the starting point; the chip color, sound, and finish are the truth.',
      'Cutting dry on steel. Most steel cuts want at least a brush-on of cutting oil to keep heat down and finish up.',
    ],
    practiceExercises: [
      {
        id: 'calculate-rpm',
        description: 'Calculate the RPM for the following without a calculator (use the × 4 shortcut): (a) 1″ mild steel, HSS, 100 SFM. (b) 0.5″ aluminum, HSS, 300 SFM. (c) 2″ brass, carbide, 600 SFM. Answers: 400, 2400, 1200 RPM. Verify each on your lathe — is the calculated speed achievable?',
        hint: 'If the calculated speed exceeds your lathe\'s max, run at max and accept a slightly lower SFM. If it\'s below the lowest gear, you may need to drop SFM to keep the cut within the machine\'s range.',
      },
      {
        id: 'diameter-shrinks-rpm',
        description: 'Set up to turn a piece of 1″ stock at the calculated RPM for your target SFM. Take a roughing cut to 0.5″. Without changing RPM, calculate the new effective SFM. Decide whether to bump RPM up before the next pass. Make the cut at the new speed and compare the chip and finish.',
        hint: 'If the chip color shifts darker after you cut down to the smaller diameter, the SFM dropped too far. Bump RPM to restore the original surface speed.',
      },
      {
        id: 'feed-comparison',
        description: 'On the same piece of stock, take two identical-depth cuts at the same RPM — one at 0.010 IPR roughing feed, one at 0.003 IPR finishing feed. Run a fingernail across both surfaces. The difference in feel is the difference between the two finish-roughness numbers from the Ra formula.',
        hint: 'The fingernail test is approximate but immediate. A measured Ra reading on a surface-finish gauge would confirm a ~10× improvement, but you can feel it.',
      },
    ],
    unlockAfterModule: 'lathe-cutting-tools',
  },
  {
    id: 'lathe-first-cut',
    number: 7,
    machine: 'lathe',
    title: 'Your First Cut — Facing & Turning to a Diameter',
    subtitle: 'Tool height, depth of cut, and reading a chip',
    description: 'The first real cut puts the previous six modules together: safe machine, clean measurement, secure workholding, correct tool, calculated speed and feed. The goal is not the finished part — it is the workflow that gets you there reliably. Make this one cut well and every cut after it has the same shape.',
    estimatedTime: '30 min',
    icon: '🪚',
    sections: [
      {
        id: 'overview',
        title: 'The procedure, top to bottom',
        content: `
          <p>You will face the end of a piece of round stock square, then turn the OD down to a target diameter. This is the foundation operation — every later lathe project (shafts, pins, fitted bushings, threads) starts with these two cuts. <a href="https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-4-turning/" target="_blank" rel="noopener">[Open Oregon — Unit 4: Turning]</a></p>
          <p>The whole sequence, before you touch the start switch:</p>
          <ol>
            <li>Identify the material. Decide your target final diameter.</li>
            <li>Mount the stock in the 3-jaw chuck. Verify chuck key removed.</li>
            <li>Install the right-hand turning tool. Set it on center, minimum overhang.</li>
            <li>Calculate RPM from the material\'s SFM and the starting diameter.</li>
            <li>Set the feed gear to a roughing feed (0.005-0.010 IPR is a safe start).</li>
            <li>Face the end square.</li>
            <li>Plan the diameter cut: how many passes, how deep each pass, with how much left for finishing.</li>
            <li>Take the roughing passes.</li>
            <li>Measure. Adjust the plan if needed.</li>
            <li>Take the finishing pass.</li>
            <li>Measure final diameter. Compare to target.</li>
          </ol>
          <p>Notice steps 1-5 are setup, 6-10 are cutting, and 11 is verification. The cutting itself is the fastest part. Everything else is preparation and feedback.</p>
        `,
        callout: {
          type: 'warning',
          title: 'First cut goes slow',
          text: 'There is no prize for finishing fast on your first turning job. Run the spindle at the calculated RPM (not max). Use manual feed for the first few passes until you can hear what "correct" sounds like. Engage power feed only once you trust the setup. A botched first cut is a learning moment; a botched second cut is bad habits.',
        },
      },
      {
        id: 'setup',
        title: 'Setup — the five things you already know how to do',
        content: `
          <p>Each previous module covered one of these. Now they happen back-to-back as a single workflow:</p>
          <ul>
            <li><strong>Safety (Module 1).</strong> Glasses on, hair back, sleeves and rings off, closed-toe shoes. E-stop reachable.</li>
            <li><strong>Workholding (Module 4).</strong> Stock in the 3-jaw with at least 1× diameter of grip and as little overhang as practical. Wipe the chuck face. Tighten. <em>Remove the chuck key.</em> Verify with your eyes that it is out and on the bench.</li>
            <li><strong>Tool (Module 5).</strong> Right-hand turning tool, mounted in the toolholder with stick-out ≤ 1.5× tool height. Set on center — verify with the rule-against-tailstock-center method or by facing a test piece and checking for a pip.</li>
            <li><strong>Measuring (Module 2).</strong> Caliper at hand, zeroed. Micrometer at hand, zeroed. Material identified by alloy (1018, 6061, brass, etc.), not just "metal".</li>
            <li><strong>Speeds and feeds (Module 6).</strong> Calculate RPM = (SFM × 4) / D using the material\'s starting SFM. Set spindle to nearest available speed. Set feed gear to ~0.005-0.010 IPR for roughing.</li>
          </ul>
          <p>Run through these as a checklist out loud. The first time, this takes ten minutes. The hundredth time, ninety seconds. The point is that nothing is skipped.</p>
        `,
      },
      {
        id: 'facing',
        title: 'Facing the end square',
        content: `
          <p>Facing produces a flat, perpendicular end on the workpiece. It cleans up the as-sawn end, gives you a known reference for length, and gives you a clean surface to set the carriage Z-zero against.</p>
          <p><strong>Procedure:</strong></p>
          <ol>
            <li>Install a facing tool (or set your turning tool so its lead angle reaches the corner). Set on center.</li>
            <li>Start the spindle at the calculated RPM.</li>
            <li>Bring the tool tip up to the as-sawn end of the work using the apron handwheel. Stop just short of touching.</li>
            <li>Lightly touch the cross-slide handwheel inward until the tool just kisses the OD of the workpiece. Note the cross-slide dial position. This is your radial reference.</li>
            <li>Crank the cross-slide outward until the tool is just clear of the OD.</li>
            <li>Advance the carriage (Z) inward by about 0.005-0.020″ — this is your facing depth-of-cut.</li>
            <li>Feed the cross-slide inward from OD toward center. Steady, controlled — power feed if you have a cross-slide feed and trust the setup, otherwise hand-feed.</li>
            <li>The tool produces a flat surface as it moves from OD to center. Watch the chip; it should be a thin continuous curl, not torn metal.</li>
            <li>When the tool reaches the center, retract it back to OD. Stop the spindle.</li>
            <li>Inspect the faced end. If you see witness marks from the saw cut, take another facing pass at the same depth. Repeat until the face is fully cleaned up.</li>
          </ol>
          <p>The finished face should be visibly flat under shop light, with no central pip (tool below center) and no scallop ring (tool was rocked or feed-inconsistent). <a href="https://tricities.wsu.edu/wp-content/uploads/WSUTC_Lathe-Operation-2-3-2018.pdf" target="_blank" rel="noopener">[WSU Tri-Cities SOP]</a></p>
        `,
        callout: {
          type: 'warning',
          title: 'A pip in the center means the tool is below center',
          text: 'If facing leaves a small raised bump at the very center, the tool was set below the spindle axis. The cut narrowed as it approached center, leaving an un-cut sliver. Re-set tool height (use the rule-against-tailstock method) and re-face. This is the most direct diagnostic for tool-height error.',
        },
      },
      {
        id: 'turning',
        title: 'Turning to a target diameter',
        content: `
          <p>Now the OD. The plan: from the starting diameter, remove enough material in roughing passes to leave 0.005-0.010″ on the diameter for a single finishing pass. The finishing pass is what hits the target dimension.</p>
          <p><strong>The dial gotcha first.</strong> The cross-slide on most lathes measures <em>radial</em> movement — how far the tool advances toward the spindle axis. But the diameter shrinks by <em>twice</em> the radial movement (you remove material from one side; the part is round, so the other side moves toward the axis by the same amount). A 0.020″ cross-slide dial reading = 0.040″ off the diameter. <a href="https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-4-turning/" target="_blank" rel="noopener">[Open Oregon — Unit 4]</a></p>
          <p>Some lathes (and most DROs in diameter mode) flip this and read the actual diameter change. Know which one yours is before you cut. Set up a quick test on scrap if you are not sure.</p>
          <p><strong>Procedure (1″ stock to 0.875″ target as the worked example — removing 0.125″ from the diameter):</strong></p>
          <ol>
            <li><strong>Plan the passes.</strong> Total to remove: 0.125″ diameter = 0.0625″ radial. Leave 0.010″ on the diameter (0.005″ radial) for finishing. So roughing removes 0.115″ diameter (0.0575″ radial). At 0.030″ radial DOC per pass, that\'s two passes (0.0575 / 0.030 ≈ 2 passes).</li>
            <li><strong>Touch off.</strong> Spindle on. Tool just clear of the OD. Carriage positioned about 0.5″ from the chuck. Cross-slide in slowly until the tool just kisses the OD — note the dial reading. This is your radial zero.</li>
            <li><strong>Retract Z, advance X by 0.030″ radial.</strong> Carriage well clear to the right. Crank cross-slide in by 0.030″ on the dial.</li>
            <li><strong>Take the cut.</strong> Engage feed (or hand-crank) to feed the carriage from right to left, toward the chuck. The tool produces a turned section of reduced diameter behind it. Watch the chip, listen to the cut.</li>
            <li><strong>End the pass</strong> at the length you want (say 0.75″ from the end). Retract the carriage to clear the work — do NOT pull the cross-slide out first, or you leave a step. Carriage back to the start position.</li>
            <li><strong>Repeat for the second roughing pass:</strong> cross-slide in another 0.030″, feed across, retract carriage.</li>
            <li><strong>Stop spindle. Measure.</strong> Use the caliper for a rough check, then the micrometer for the actual diameter. Diameter should be ~0.885″ (0.115″ down from 1.000″).</li>
            <li><strong>Plan the finish pass.</strong> Target 0.875″, currently at ~0.885″. Need to remove 0.010″ from diameter = 0.005″ radial. Cross-slide in by 0.005″.</li>
            <li><strong>Finish pass.</strong> Drop feed to 0.002-0.003″ IPR for surface finish. Feed across at the new lighter feed. Listen — the cut should sound steady and the chip should be thin.</li>
            <li><strong>Stop spindle. Measure.</strong> Should be at or within ±0.0005″ of 0.875″. If you overshot (too small), there\'s no fix; if undershot, take another light pass at the same feed.</li>
          </ol>
        `,
      },
      {
        id: 'inspect-and-learn',
        title: 'Inspect, record, and what good looks like',
        content: `
          <p>The cut is done. Now the part teaches you.</p>
          <ul>
            <li><strong>Final diameter</strong> — write it down. Did you hit target? Within how much?</li>
            <li><strong>Surface finish</strong> — visually shiny and uniform? Or banded, torn, or scaly? Bands often mean inconsistent feed; torn means the tool is dull or off-center; scaly means feed too high for the nose radius.</li>
            <li><strong>Chip evidence</strong> — what did the chips look like during the roughing passes vs the finishing pass? Color (silver / straw / blue), shape (curl / spring / stringy), and behavior (broke cleanly / wrapped the tool) all tell you what was happening at the cut.</li>
            <li><strong>What surprised you</strong> — every first cut has a surprise. The tool grabbed harder than expected when you engaged feed; the dial math caught you off guard; the finish was rougher than you hoped. Write it down. Next time it won\'t be a surprise.</li>
          </ul>
          <p>Use the practice exercise checkboxes to log the cut. The notes field is where this becomes a personal reference — six months from now, "I learned that 1018 at 100 SFM with a worn HSS tool gives a torn finish" is more valuable than any textbook chart.</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue — the lathe-track capstone',
          text: 'A first cut that goes well looks boring. The spindle hums at the calculated RPM, the chip curls into thin shiny coils, the carriage feeds steadily, the cross-slide stays still during the pass, and the part comes out within a few thou of target on the first try. If that is what your cut looked like, you have the lathe track. If not, the exercise notes are where you figure out which module to revisit.',
        },
      },
    ],
    quiz: [
      {
        id: 'doc-vs-diameter',
        type: 'multiple-choice',
        question: 'Your cross-slide dial reads radius (not diameter). You crank in 0.020″ on the dial. By how much does the workpiece diameter shrink?',
        options: [
          '0.010″',
          '0.020″',
          '0.040″',
          '0.080″',
        ],
        correctIndex: 2,
        explanation: 'The dial moved the tool 0.020″ toward the spindle axis (one side). The part is round, so material comes off that side — and since you measure diameter side-to-side, the part shrinks by 2× the radial movement = 0.040″. Lathes with diameter-reading dials (and DROs in diameter mode) hide this math; standard radius dials do not.',
      },
      {
        id: 'facing-pip',
        type: 'multiple-choice',
        question: 'You face the end of a piece of stock and a small raised pip is left in the center. What does that tell you?',
        options: [
          'Spindle speed was too high',
          'Tool is set below the spindle centerline',
          'Cross-slide feed was too fast',
          'The workpiece is hardened steel',
        ],
        correctIndex: 1,
        explanation: 'A pip in the center is the most direct symptom of tool-below-center. As the tool approaches the spindle axis, its effective cutting geometry narrows; below center, the tool stops cutting cleanly and leaves an un-machined nub. Re-set tool height and re-face.',
      },
      {
        id: 'finish-allowance',
        type: 'multiple-choice',
        question: 'When planning the cut sequence to a target diameter, how much material should you typically leave for the final finishing pass?',
        options: [
          '0.001 - 0.002″ on the diameter',
          '0.005 - 0.010″ on the diameter',
          '0.020 - 0.030″ on the diameter',
          'Take it all off in one finishing pass',
        ],
        correctIndex: 1,
        explanation: 'Leaving 0.005-0.010″ on the diameter (0.0025-0.005″ radial) lets the finish pass run light enough for a clean surface but heavy enough that the tool actually cuts instead of rubbing. Less than ~0.005″ and the tool may rub; more and surface finish suffers.',
      },
      {
        id: 'when-to-measure',
        type: 'multiple-choice',
        question: 'During a turning operation to a target diameter, the right time to take your first micrometer measurement is:',
        options: [
          'Before any cutting, on the raw stock only',
          'During the final finishing pass while the spindle is still running',
          'After roughing is complete, before planning the finishing pass',
          'Only after the part is removed from the chuck',
        ],
        correctIndex: 2,
        explanation: 'Stop the spindle and measure after roughing — this tells you where you actually are vs where the plan said you would be. Adjust the finish-pass DOC if needed. Never measure with the spindle running. Measuring after part removal is too late to correct anything.',
      },
      {
        id: 'retract-order',
        type: 'multiple-choice',
        question: 'At the end of a turning pass, the correct order is:',
        options: [
          'Retract the cross-slide first, then move the carriage back',
          'Stop the spindle, then retract everything',
          'Move the carriage back to the start position with the cross-slide unchanged, then retract the cross-slide if needed for the next pass',
          'Disengage feed only — leave the tool where it is',
        ],
        correctIndex: 2,
        explanation: 'If you retract the cross-slide before backing the carriage off, you leave a tapered step along the work where the tool moved out mid-pass. Carriage first (Z back to start), then adjust cross-slide for the next pass.',
      },
    ],
    videos: [
      {
        title: 'Metal Lathe Tutorial: Your First Project',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks metal lathe tutorial first project',
        description: 'Quinn walks through a complete first turning job from setup through finished part. Watch this once before your own first cut.',
      },
      {
        title: 'Facing and Turning — Lathe Basics',
        channel: 'mrpete222 / Tubalcain',
        searchQuery: 'Tubalcain mrpete222 facing turning lathe basics',
        description: 'Tubalcain\'s shop-class take on the two foundational operations. Particularly good on the dial math and what to do when you have to undo a mistake.',
      },
    ],
    sources: [
      {
        label: 'Open Oregon Manufacturing Processes 4-5 — Unit 4: Turning',
        url: 'https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-4-turning/',
      },
      {
        label: 'Open Oregon Manufacturing Processes 4-5 — Unit 2: Speed and Feed',
        url: 'https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-2-speed-and-feed/',
      },
      {
        label: 'WSU Tri-Cities — Manual Toolroom Lathes SOP (PDF)',
        url: 'https://tricities.wsu.edu/wp-content/uploads/WSUTC_Lathe-Operation-2-3-2018.pdf',
      },
      {
        label: 'UCLA SHOPS — Lathe SOP',
        url: 'https://www.seasshops.ucla.edu/standard-operating-procedure-sop-lathe/',
      },
      {
        label: 'U.S. Army TC 9-524 Ch. 7 — Lathes (PDF, hosted by CMU)',
        url: 'https://uhv.cheme.cmu.edu/procedures/machining/ch7.pdf',
      },
    ],
    toolsNeeded: [
      'Your lathe with 3-jaw chuck mounted (Module 4)',
      'A right-hand turning tool, set on center (Module 5)',
      'A facing tool (or a turning tool whose lead angle reaches the corner cleanly)',
      'A piece of round stock for the cut — 1″ diameter mild steel (1018) is the classic teaching material',
      'Caliper and 0-1″ micrometer (Module 2)',
      'Cutting oil — a small brush-on bottle for mild steel',
      'A planned target diameter (the worked example uses 1″ → 0.875″)',
      'Chip brush, clean rag',
    ],
    preChecklist: [
      'All previous-module setup steps complete (safety, workholding, tool installation, measuring tools ready, RPM/feed calculated)',
      'Chuck key removed and on the bench — verified by eye',
      'Tool on center — verified by rule-against-tailstock-center or facing test',
      'Calculated RPM set on the spindle; feed gear set to roughing feed (~0.005-0.010 IPR)',
      'A written plan: starting diameter, target diameter, number of roughing passes, finishing-pass allowance',
      'You know whether your cross-slide dial reads radius or diameter — and your DRO mode if equipped',
      'Caliper and mic at the bench, zeroed',
      'E-stop reachable',
    ],
    commonMistakes: [
      'Treating the cross-slide dial as diameter when it actually reads radius (or vice versa). Diameter shrinks by 2× the radial dial movement on a standard dial.',
      'Retracting the cross-slide mid-pass and leaving a tapered step on the work. Carriage moves back first; cross-slide adjusts for the next pass.',
      'Skipping the post-roughing measurement and going straight to the finish pass. The plan said you would be at 0.885″ — measure to confirm before committing to the final cut.',
      'Taking a finish pass under ~0.005″ DOC. The tool rubs instead of cuts; the surface ends up worse than a heavier finish pass would have given.',
      'Measuring with the spindle running. Stop, retract, then measure. Never reach toward a rotating workpiece.',
      'Forgetting that SFM drops as the diameter shrinks. By the time you are at the finish pass, you may be at half the original SFM — bump RPM if the chip color shifts.',
    ],
    practiceExercises: [
      {
        id: 'face-square',
        description: 'Mount a piece of round stock (1″ × 2″ minimum) in the chuck with ~1″ sticking out. Face the end with light passes (0.005-0.010″ DOC) from OD to center until all witness marks from the saw cut are gone. Inspect the finished face — flat, no pip, no scallop.',
        hint: 'A pip in the center = tool below center. A scallop ring = inconsistent feed (slow down and try power feed on the cross-slide). A torn finish = dull tool or wrong SFM.',
      },
      {
        id: 'turn-to-target',
        description: 'Starting from the same piece (or a fresh one), turn the OD down to 7/8″ (0.875″) over a ~0.75″ length. Plan: two roughing passes at ~0.030″ radial DOC, leave 0.010″ on the diameter, finish at 0.005″ radial DOC with finer feed. Measure after roughing, adjust if needed, finish. Final dimension goal: 0.875″ ±0.002″.',
        hint: 'Write the plan down before you start. RPM, feed for roughing, feed for finishing, DOC per pass, intermediate measurement target, finishing DOC. Cross out and adjust as you go — that\'s the learning.',
      },
      {
        id: 'reflect',
        description: 'In the notes for this exercise, record: (a) the actual final diameter you measured, (b) the surface finish vs your expectation, (c) one thing that surprised you during the cut, and (d) which previous module you would re-read if you redid this part tomorrow.',
        hint: 'The "one thing that surprised you" prompt is the most valuable. Six months from now this note will be more useful than any textbook page.',
      },
    ],
    unlockAfterModule: 'lathe-speeds-feeds',
  },

  // ── Mill track ───────────────────────────────────────────────────────
  {
    id: 'mill-anatomy',
    number: 3,
    machine: 'mill',
    title: 'Mill Anatomy & Controls',
    subtitle: 'Knee, saddle, table, quill, spindle, power feed, DRO',
    description: 'Before you mount a vise or chuck up an end mill you need to name every casting on a Bridgeport-style knee mill and know which handwheel moves what direction. The mill has three independent linear axes and a separate quill feed — four motions to confuse before you cut anything.',
    estimatedTime: '20 min',
    icon: '⚙️',
    sections: [
      {
        id: 'overview',
        title: 'The major castings — column, knee, saddle, table, head',
        content: `
          <p>A Bridgeport-style vertical knee mill has five major castings stacked into a "C" shape. From bottom to top: <strong>base</strong>, <strong>column</strong>, <strong>knee</strong>, <strong>saddle</strong>, <strong>table</strong>. The <strong>head</strong> hangs off the top of the column. <a href="https://me.berkeley.edu/wp-content/uploads/2020/09/Bridgeport-Vertical-Mill-Manual.pdf" target="_blank" rel="noopener">[Bridgeport Manual — UC Berkeley]</a></p>
          <ul>
            <li><strong>Base.</strong> The cast-iron foot the whole machine sits on. Holds coolant if you have a flood system.</li>
            <li><strong>Column.</strong> The vertical pillar that everything hangs from. Has a precision dovetail way on the front for the knee to slide up and down.</li>
            <li><strong>Knee.</strong> The first sliding casting — moves up and down the column on the dovetail ways. The knee handwheel cranks this through a leadscrew. This is your Z-axis on most knee mills.</li>
            <li><strong>Saddle.</strong> Sits on top of the knee. Slides in and out (toward and away from the column) — Y axis.</li>
            <li><strong>Table.</strong> Sits on top of the saddle. Slides left and right — X axis. The top surface is the work-holding area with T-slots running the length of the table.</li>
            <li><strong>Head.</strong> Mounted to the top of the column, typically nodding (Bridgeport "ram" style can tilt forward, swivel, and nod). Houses the motor, spindle, and quill.</li>
          </ul>
          <p>Knee mills come in many sizes and configurations, but every one is built around this same vocabulary. If you can stand at the operator's position and name and point to each casting, you have the foundation. <a href="https://www.engineering.pitt.edu/contentassets/f57e074f743140679d43ad3d2b55a8d4/2-bridgeport-style-milling-machines.pdf" target="_blank" rel="noopener">[Pitt Engineering — Bridgeport-style mills]</a></p>
        `,
        callout: {
          type: 'warning',
          title: 'Pinch points — the table is the worst',
          text: 'The table moves a long way (commonly 30-42″ on a Bridgeport-class mill) and weighs hundreds of pounds when loaded. Power-fed it does not stop because you are in its way. Know where every casting is going before you engage any feed. The space between the table end and the column under power feed is a real injury risk.',
        },
      },
      {
        id: 'head',
        title: 'The head — spindle, quill, drawbar',
        content: `
          <p>The head contains the motor, the spindle, and the quill. Bridgeport-style heads also have controls for spindle speed (variable-speed dial and / or back-gear lever), spindle direction (forward / off / reverse), spindle brake, and quill feed.</p>
          <ul>
            <li><strong>Spindle.</strong> The rotating shaft that grips and turns the cutting tool. On most Bridgeports the spindle taper is R8; on heavier mills it is NMTB-30/40, BT30, or similar. The taper accepts collets, end-mill holders, drill chucks, and arbors.</li>
            <li><strong>Drawbar.</strong> A long bolt that runs from the top of the head down through the spindle, threading into the back of whatever tool / collet is mounted. Tightening the drawbar pulls the tool firmly into the spindle taper. Loosening it (and tapping with a soft-face mallet) releases the tool.</li>
            <li><strong>Quill.</strong> A sliding sleeve around the spindle that lets the spindle (and tool) extend down into the work, like a drill press. Travel is typically 4-5″. The quill is fed by hand with the <strong>quill feed handle</strong> (the big lever on the right of the head) or by power for drilling.</li>
            <li><strong>Quill lock.</strong> A small knob on the head that clamps the quill in position. <strong>For milling, the quill should be retracted to its upper limit and locked.</strong> Leave it unlocked and milling forces will deflect the quill, ruining the cut.</li>
          </ul>
          <p>The head can usually nod forward and backward and swivel side-to-side for angled work, but for foundation milling keep it set to vertical (tram square — covered in the workholding module).</p>
        `,
      },
      {
        id: 'axes',
        title: 'The three linear axes — X, Y, Z (and how the quill complicates Z)',
        content: `
          <p>A Bridgeport knee mill has three orthogonal linear motions, each with its own handwheel and dial:</p>
          <ul>
            <li><strong>X-axis (table, left-right).</strong> The longest travel — typically 30-42″. The table handwheel is on the right end of the table (and sometimes mirrored on the left for two-handed operation). Power-fed by an X-axis feed motor on many mills.</li>
            <li><strong>Y-axis (saddle, in-out).</strong> Typically 12-18″ travel. The Y handwheel is on the front of the saddle, below the table. Less commonly power-fed; usually hand-cranked.</li>
            <li><strong>Z-axis (knee, up-down).</strong> The knee crank is the large handwheel on the front of the knee, low and to the right. Cranks the entire knee (and everything above it) up or down the column. This is your <em>primary</em> Z control for setting cut depth.</li>
          </ul>
          <p><strong>The quill complicates Z.</strong> The quill provides a separate, fine, short-travel up-down motion of the spindle (and tool) independent of the knee. Two ways to "lower the tool into the work":</p>
          <ul>
            <li><strong>Raise the knee:</strong> moves the whole table and workpiece up toward the tool. Coarse but very rigid (the knee is locked solid against the column). This is what you use to set milling depth-of-cut.</li>
            <li><strong>Lower the quill:</strong> moves the tool down toward the work. Fine and convenient for drilling, but less rigid (the quill is a sliding fit). Use the quill for drilling and tapping; lock it for milling.</li>
          </ul>
          <p>This is the most common source of beginner confusion on knee mills. The rule: <strong>quill for drilling, knee for milling.</strong> <a href="https://kb.wisc.edu/engr/ecbshops/123365" target="_blank" rel="noopener">[U-Wisconsin Eisen Mill Guide]</a></p>
        `,
        callout: {
          type: 'warning',
          title: 'Lock the axes you are not using',
          text: 'Each axis has a clamp (X, Y, knee, and quill all have separate locks). During a milling cut, lock every axis except the one you are feeding. An unclamped axis under cutting force will drift — small drift becomes a tapered cut, big drift can dig the tool into the work. The locks live on the saddle, knee, and quill housings.',
        },
      },
      {
        id: 'controls',
        title: 'Power feeds, spindle controls, and the quill stop',
        content: `
          <p>Beyond the handwheels, a Bridgeport-style mill has several powered and adjustable controls:</p>
          <ul>
            <li><strong>Spindle speed.</strong> Most Bridgeports have a variable-speed pulley with a hand crank that changes effective RPM — <em>only adjust while the spindle is running.</em> Heavier mills use a gearbox and step pulleys. Many older machines have a back-gear lever for low-speed high-torque operation.</li>
            <li><strong>Spindle direction.</strong> Forward / off / reverse lever. End mills are directional (covered in Cutting Tools — Mill); reverse is mostly for tap retraction.</li>
            <li><strong>Spindle brake.</strong> A lever or knob that brings the spindle to a stop quickly after the motor is off. Useful but does not replace the off switch.</li>
            <li><strong>X-axis power feed.</strong> A motor mounted on the end of the table that drives the table left-right at a selectable feed rate. Has a directional lever (left / off / right) and a feed-rate knob. Trip dogs on the table can stop the feed at preset positions.</li>
            <li><strong>Quill feed and quill stop.</strong> A small lever selects manual / fine power feed on the quill — used for drilling. The quill stop is a depth-setting collar that limits how far the quill can travel down, so you can drill to a repeatable depth.</li>
            <li><strong>Coolant pump.</strong> If your mill has flood coolant, the pump is usually switched separately. Aim, then turn on.</li>
          </ul>
        `,
      },
      {
        id: 'dro',
        title: 'Three-axis DROs and datum management',
        content: `
          <p>A mill DRO is more useful than a lathe DRO simply because there are three axes to manage. A typical mill DRO shows X (table), Y (saddle), and Z (knee) position to 0.0005″ or 0.0001″ resolution. <a href="https://en.wikipedia.org/wiki/Digital_readout" target="_blank" rel="noopener">[Wikipedia — DRO]</a></p>
          <p>The two concepts that matter most on a mill DRO:</p>
          <ul>
            <li><strong>Datum (origin) setting.</strong> Before you cut anything, you set the X-Y-Z zero of the DRO to a meaningful point on the workpiece — usually a corner, the center of a feature, or a reference edge. From then on, every position on the DRO is relative to that datum. A drawing dimensioned from "the lower-left corner" becomes a literal X+Y+ position you can dial to.</li>
            <li><strong>Absolute vs incremental modes.</strong> Absolute tracks position from the datum you set. Incremental zeros wherever you press the key and tracks from there — useful for stepping a bolt-hole pattern without doing the math on each hole. Most mill DROs let you flip between them or keep both available. <a href="https://www.touchdro.com/resources/dro-manual/dro-coordinate-system-and-datums.html" target="_blank" rel="noopener">[TouchDRO — Coordinate Systems]</a></li>
          </ul>
          <p>The DRO does not change <em>how</em> the mill cuts — it changes how you keep track of where the tool is. Without a DRO, you count handwheel turns and trust the dial. With one, you read a number. For multi-feature parts (hole patterns, slots at known coordinates) the DRO removes the bookkeeping that beginners get wrong.</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue — datum first, cut second',
          text: 'Before any mill operation, decide where your X-Y zero will be and set it. Edge-find or indicator-touch the reference, zero the axis, and write the datum down in your notes ("X-zero = left edge of vise jaw, Y-zero = front face of work"). Once the datum is set, every drawing dimension becomes a coordinate to dial to. Datum drift mid-job means you have to start the dimensioning over — costly.',
        },
      },
    ],
    quiz: [
      {
        id: 'castings',
        type: 'multiple-choice',
        question: 'Which of these is NOT one of the major castings of a Bridgeport-style knee mill?',
        options: [
          'Knee',
          'Saddle',
          'Table',
          'Spindle',
        ],
        correctIndex: 3,
        explanation: 'The spindle is a sub-component of the head, not a major casting. The five major castings stack as: base, column, knee, saddle, table — with the head mounted to the top of the column.',
      },
      {
        id: 'axes',
        type: 'multiple-choice',
        question: 'On a standard knee mill, which casting moves to provide the Y-axis motion?',
        options: [
          'The table (slides left-right on the saddle)',
          'The saddle (slides in-out on the knee)',
          'The knee (slides up-down on the column)',
          'The head (nods forward-back)',
        ],
        correctIndex: 1,
        explanation: 'The saddle is the middle casting in the X-Y-Z stack. It slides in and out (toward and away from the column) on the knee, providing Y-axis motion. The table sits on the saddle and provides X; the knee provides Z.',
      },
      {
        id: 'quill-vs-knee',
        type: 'multiple-choice',
        question: 'You need to take a milling cut at a precise depth using an end mill. Which control should you use to set the depth?',
        options: [
          'Lower the quill with the quill feed handle, then lock it',
          'Raise the knee using the knee crank, with the quill retracted and locked',
          'Lower the head by loosening the column ram',
          'Either knee or quill — both are equally rigid',
        ],
        correctIndex: 1,
        explanation: 'Rule: quill for drilling, knee for milling. The knee locked against the column is far more rigid than the sliding quill. Milling forces will deflect an unlocked quill and ruin the cut. Retract the quill, lock it, and set depth with the knee.',
      },
      {
        id: 'axis-locks',
        type: 'true-false',
        question: 'During a milling cut, you should lock every axis except the one you are feeding.',
        options: ['True', 'False'],
        correctIndex: 0,
        explanation: 'True. Unclamped axes drift under cutting forces — small drift produces a taper, large drift can dig the tool into the work. Lock X, Y, knee, and quill (whichever you are not actively moving) before each cut.',
      },
      {
        id: 'dro-datum',
        type: 'multiple-choice',
        question: 'You\'re about to drill four holes at coordinates dimensioned from the lower-left corner of a workpiece. The right first DRO step is:',
        options: [
          'Zero all three axes wherever the table happens to be',
          'Find the lower-left corner with an edge finder, set X and Y to zero there, then dial the table to each hole\'s coordinates',
          'Center-drill all four holes by eye first, then read the DRO',
          'Skip the DRO and count handwheel turns',
        ],
        correctIndex: 1,
        explanation: 'Datum first, cut second. Set the DRO origin to the drawing\'s reference (lower-left corner here), then every dimension becomes a direct table-position you can dial to. Without setting the datum, the DRO reads positions that mean nothing relative to your part.',
      },
    ],
    videos: [
      {
        title: 'Bridgeport Milling Machine Tutorial — Anatomy & Controls',
        channel: 'mrpete222 / Tubalcain',
        searchQuery: 'Tubalcain mrpete222 Bridgeport mill anatomy controls',
        description: 'Tubalcain\'s shop-class walkthrough of a Bridgeport. The best straightforward overview of every control on the machine.',
      },
      {
        title: 'Using a DRO on a Mill — Datums and Hole Patterns',
        channel: 'Joe Pieczynski',
        searchQuery: 'Joe Pieczynski mill DRO datum bolt pattern',
        description: 'Joe Pie demonstrates real datum-setting and absolute-vs-incremental on a mill DRO. Watch this before your first DRO-equipped job.',
      },
    ],
    sources: [
      {
        label: 'Hardinge / Bridgeport Series I Mill Manual (PDF, hosted by UC Berkeley)',
        url: 'https://me.berkeley.edu/wp-content/uploads/2020/09/Bridgeport-Vertical-Mill-Manual.pdf',
      },
      {
        label: 'Bridgeport Series I Mill Manual (PDF, MIT Fab Lab copy)',
        url: 'https://fab.cba.mit.edu/content/tools/bridgeport_mill/Bridgeport.pdf',
      },
      {
        label: 'University of Pittsburgh — Bridgeport-style Milling Machines (PDF)',
        url: 'https://www.engineering.pitt.edu/contentassets/f57e074f743140679d43ad3d2b55a8d4/2-bridgeport-style-milling-machines.pdf',
      },
      {
        label: 'TouchDRO — DRO Coordinate Systems and Datums',
        url: 'https://www.touchdro.com/resources/dro-manual/dro-coordinate-system-and-datums.html',
      },
      {
        label: 'Wikipedia — Digital Readout (DRO)',
        url: 'https://en.wikipedia.org/wiki/Digital_readout',
      },
    ],
    toolsNeeded: [
      'Your mill (power off, locked out for the walk-around)',
      'The manufacturer\'s manual for your specific mill — Bridgeport-style machines vary in details across decades',
      'A clean rag and a bright light',
      'Optional: phone camera to label parts as you identify them',
    ],
    preChecklist: [
      'Power off, plug pulled or breaker locked out — this is exploration, not running',
      'Spindle stationary; quill retracted to its upper limit and locked',
      'Table parked roughly centered in X-Y so it can move either direction',
      'All wrenches, drawbar wrenches, edge finders, and rags removed from machine surfaces',
      'Coolant pump off if equipped; coolant tray clear of debris',
      'Knee handwheel positioned so you can see and reach it',
    ],
    commonMistakes: [
      'Confusing X (table) and Y (saddle) handwheels. They are perpendicular; cranking the wrong one moves the work in the wrong direction.',
      'Using the quill for milling depth instead of the knee. Quill flex under side-load ruins the cut. Quill for drilling; knee for milling.',
      'Leaving the quill unlocked during a milling pass. Same problem — the quill drifts under cutting force.',
      'Not locking the other axes during a cut. An unclamped X or Y under cutting load produces tapered work and surprise dig-ins.',
      'Adjusting variable-speed pulley while the spindle is OFF on a Bridgeport-style head. The belt position needs spindle rotation to shift; adjusting cold can damage the variator.',
      'Forgetting which DRO datum you set. Write it down — "X-zero = left edge of vise jaw, Y-zero = front face of work, Z-zero = top of part" — before you start cutting.',
    ],
    practiceExercises: [
      {
        id: 'name-the-castings',
        description: 'Stand at the operator\'s position. Point to and name aloud each major casting: base, column, knee, saddle, table, head. Then point to and name each handwheel: X (table), Y (saddle), Z (knee crank), quill feed.',
        hint: 'Repeat until automatic. If you have to think about which handwheel is which, you have not learned the vocabulary yet. The cost of confusion under power is real.',
      },
      {
        id: 'axis-jog-drill',
        description: 'With the spindle OFF, jog each linear axis through several inches of its travel using its handwheel. Note: travel per revolution of the dial, direction (which way moves the tool toward / away from the work), and the location of each axis lock. Engage and release each lock to feel it.',
        hint: 'Most Bridgeport-class X and Y handwheels are 0.200″ per turn (5 TPI screw). The knee is typically slower per turn — sometimes 0.100″. Confirm on your machine.',
      },
      {
        id: 'dro-trust-check',
        description: 'If your mill has a DRO: with the spindle off, jog each axis a known distance using the handwheel dial (say 1.000″ on X). Confirm the DRO moved by the same amount. Repeat for Y and Z. Set a datum on a feature (vise jaw corner is fine) and confirm the DRO reads zero there, then jog to a known coordinate and verify.',
        hint: 'No DRO? Skip this one. But still verify your X and Y dial calibration matches what the handwheel says it should — many older mills have backlash that bites you in the dial reading.',
      },
    ],
    unlockAfterModule: 'measuring',
  },
  {
    id: 'mill-workholding',
    number: 4,
    machine: 'mill',
    title: 'Workholding — Vise Basics & Edge Finding',
    subtitle: 'Tramming a vise, parallels, edge finder vs. dial indicator',
    description: 'Mill workholding has more setup steps than lathe workholding because the workpiece is rectangular, the cutting forces come from all directions, and you have to tell the DRO where the work actually is. Tram the vise, mount the work flat, find the edges. Skip any one of these and the part you cut will not match the drawing.',
    estimatedTime: '25 min',
    icon: '🔧',
    sections: [
      {
        id: 'tramming',
        title: 'Mounting and tramming the vise',
        content: `
          <p>A milling vise mounts to the table with T-slot bolts (or studs and step blocks) that pass through the vise base into the T-slots in the table. Most shops keep the vise mounted in a fixed orientation with the <strong>fixed jaw parallel to the X axis</strong> — that way, any cut you take along X is square to the fixed jaw, and edge-finding off the fixed jaw gives you a true reference.</p>
          <p>"Tramming the vise" is the procedure of making the fixed jaw exactly parallel to the X-axis travel. Without tramming, every cut you make picks up the angular error between the jaw and the table travel.</p>
          <p><strong>Procedure:</strong></p>
          <ol>
            <li>Wipe the vise base and the table mating surface clean — no chips, no coolant residue.</li>
            <li>Set the vise on the table roughly parallel to X. Hand-snug the T-bolts but do not torque them down.</li>
            <li>Mount a dial test indicator in the spindle with the contact tip facing the fixed jaw.</li>
            <li>Lower the spindle (quill or knee) until the indicator tip just contacts the fixed jaw face. Pre-load it about half a revolution. Zero the bezel.</li>
            <li>Traverse the table along X. Read the indicator at the far end of the jaw.</li>
            <li>If the reading is not zero, tap the back of the vise body with a soft-face mallet to rotate it. Move toward zero by half the indicated error each tap.</li>
            <li>Re-traverse, re-tap, iterate. Goal: under 0.0005″ across the full fixed jaw length.</li>
            <li>Tighten the T-bolts in a cross pattern (not one at a time) so the vise does not twist out of tram during clamping.</li>
            <li>Re-check tram after final tightening. Adjust if needed. <a href="https://dspace.mit.edu/bitstream/handle/1721.1/56578/2-007Spring-2005/NR/rdonlyres/Mechanical-Engineering/2-007Spring-2005/5DF905BB-E519-4BEB-9101-58D918C19F62/0/intro.pdf" target="_blank" rel="noopener">[MIT 2.007 — Milling intro]</a></li>
          </ol>
        `,
        callout: {
          type: 'warning',
          title: 'Tighten the T-bolts in a pattern, not one at a time',
          text: 'Cranking one T-bolt fully tight before the other twists the vise out of tram by tens of thousandths. Snug both, then alternate small-tighten passes until both are at full torque. Re-check tram after — a 0.001″ shift during clamping is common; a 0.005″ shift means the vise base was not flat on the table.',
        },
      },
      {
        id: 'parallels',
        title: 'Parallels and the "fixed jaw" reference',
        content: `
          <p>A mill vise has two jaws: a <strong>fixed jaw</strong> (welded or pinned to the vise body) and a <strong>movable jaw</strong> (slides on the screw). The fixed jaw is your reference — it does not move, it is trammed parallel to X, and every edge-find and clamping pressure relates back to it. The movable jaw just supplies pressure.</p>
          <p><strong>Parallels</strong> are matched pairs of precision-ground rectangular bars that sit in the bottom of the vise to support the workpiece <em>above</em> the vise bottom. Reasons you almost always use them:</p>
          <ul>
            <li><strong>Raise the work into the jaws.</strong> The vise jaws are about 1.5″ tall on a standard Bridgeport vise; a 1/2″ tall workpiece sits below the top of the jaws unless lifted by parallels.</li>
            <li><strong>The vise bottom is not a reference surface.</strong> It catches chips and is not necessarily flat to the jaws. Parallels are ground precision-flat and parallel to each other (within 0.0002″ on a decent set).</li>
            <li><strong>Lets you machine the full top of the work</strong> without hitting the jaw tops.</li>
            <li><strong>Gives the bottom of the work a known reference</strong> for setting Z — touch the top of the work, subtract part thickness, you know where the parallel surface is.</li>
          </ul>
          <p>Pick the tallest parallels that still leave enough work above the jaws for the cut you need. Match the pair carefully — using mismatched parallels (front taller than back, even by 0.001″) tilts the work and ruins flatness. <a href="https://ise.ncsu.edu/processes/wp-content/uploads/sites/11/2013/08/ToolingWorkholding-ES0350.pdf" target="_blank" rel="noopener">[NCSU — Tooling and Workholding]</a></p>
        `,
      },
      {
        id: 'clamping',
        title: 'Clamping — pressure, lift-off, and the dead-blow tap',
        content: `
          <p>Clamping a workpiece in a mill vise is two motions: tighten the vise, then tap the work down. Both are required.</p>
          <p>Here is the gotcha: when the movable jaw squeezes, it does not push <em>perfectly horizontal</em>. Tiny flex in the jaw and screw lift the workpiece slightly off the parallels — sometimes 0.005-0.020″. This is called <strong>jaw lift</strong> and it is the most common reason a milled top surface is not parallel to the bottom. <a href="https://pure.psu.edu/en/publications/the-impact-of-rigid-body-workpiece-displacement-on-the-geometric-/" target="_blank" rel="noopener">[Penn State — workpiece displacement research]</a></p>
          <p><strong>Procedure:</strong></p>
          <ol>
            <li>Place the workpiece on the parallels, against the fixed jaw, roughly centered.</li>
            <li>Tighten the vise to <em>moderate</em> pressure first — enough to hold but not full torque.</li>
            <li>Tap the workpiece down onto the parallels with a soft-face mallet (a "dead-blow" with shot inside the head is ideal). Tap both sides; you should hear the dull "thunk" change to a sharper sound as the work seats fully.</li>
            <li>Test the parallels: try to slide each parallel by hand. If a parallel moves easily, the work is still lifted off it. Keep tapping until both parallels are pinched tight.</li>
            <li>Now apply full clamping pressure with the vise handle. Re-test the parallels — both should be locked solid.</li>
            <li>If either parallel still moves after full tightening, the work is sitting on something (chip, burr, dirt) or the vise is twisting. Unclamp, clean, restart.</li>
          </ol>
          <p>For thin or fragile work, "soft jaws" (aluminum or copper jaw plates) protect the surface from jaw marks. For irregular shapes (round, hex, sloped), specialty jaws or fixturing replaces the standard flat jaws.</p>
        `,
        callout: {
          type: 'warning',
          title: 'If the parallels move, the work is not seated',
          text: 'The parallel-pinch test is the single most reliable check that your work is sitting flat against the parallels. Pinched solid = clamped flat. Moves under finger pressure = lifted off, and your top surface will not come out parallel to the bottom. Stop and tap until both parallels lock.',
        },
      },
      {
        id: 'edge-finding-mechanical',
        title: 'Edge finding — mechanical (wiggler-type)',
        content: `
          <p>A <strong>mechanical edge finder</strong> is a small tool that mounts in the spindle. It has a precision-ground tip (usually 0.200″ diameter) that swings off-axis until it contacts the workpiece, at which point it "kicks" sharply back into concentricity. The moment of the kick is when the tip contacts the work — that is your X or Y reference. <a href="https://en.wikipedia.org/wiki/Wiggler_(tool)" target="_blank" rel="noopener">[Wikipedia — Wiggler]</a></p>
          <p><strong>Procedure (X-axis edge):</strong></p>
          <ol>
            <li>Mount the edge finder in a collet or end-mill holder. Tighten the drawbar.</li>
            <li>Run the spindle at about 600-1000 RPM (above 1000 the tip flies out by centrifugal force; below 400 it does not "settle" cleanly).</li>
            <li>Deflect the tip about 1/8″ off-center with your finger so it wobbles visibly.</li>
            <li>Position the spindle off to one side of the workpiece edge you want to find. Lower the knee so the tip is alongside the edge.</li>
            <li>Slowly feed the table (or saddle) toward the work. The tip wobbles harmlessly until it contacts the work, at which point it kicks sharply back into concentricity. <strong>Stop the moment of the kick</strong> — that is the contact point.</li>
            <li>The edge of the workpiece is <strong>not</strong> at the spindle centerline now — it is 0.100″ away (half the 0.200″ tip diameter). Move the table 0.100″ away from the work, then zero your DRO X-axis. Now X-zero is exactly on the workpiece edge.</li>
          </ol>
          <p>Do the same for the Y-axis. Two edge-finds give you a corner-referenced datum.</p>
        `,
      },
      {
        id: 'edge-finding-electronic',
        title: 'Edge finding — electronic, plus datum setting',
        content: `
          <p>An <strong>electronic edge finder</strong> works on a different principle. It is a battery-powered tool with a conductive tip; when the tip touches the (electrically conductive) workpiece, it closes a circuit and lights an LED (and / or buzzes). The spindle does not need to rotate. <a href="https://www.sme.org/technologies/articles/2019/august/finding-the-edge/" target="_blank" rel="noopener">[SME — Finding the Edge]</a></p>
          <p>Advantages: no RPM management, no kick to read, works on non-conductive setups with extra care, and many electronic edge finders have a smaller tip (often 0.200″ but some are 0.250″ — check yours) with the same kind of half-diameter offset.</p>
          <p><strong>Procedure is similar:</strong> mount, position alongside the edge, slowly feed toward the work, stop when the LED lights, apply the half-diameter offset to move to the edge, zero the DRO axis.</p>
          <p><strong>Datum setting from two edges.</strong> A typical workflow:</p>
          <ol>
            <li>Edge-find the left side of the work. Apply the offset (typically 0.100″). Zero X.</li>
            <li>Move the spindle to the front of the work. Edge-find the front edge. Apply the offset. Zero Y.</li>
            <li>You now have X-Y origin at the lower-left corner of the part. Every dimension on the drawing becomes a coordinate you can dial to.</li>
            <li>Set Z-zero by touching the top of the workpiece with the edge finder (electronic) or a feeler / paper (mechanical) — covered in detail in the first-cut module.</li>
          </ol>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue — verify the datum',
          text: 'After setting X and Y zero from your edge-finds, traverse the table to a known feature — the opposite edge of the work, or a hole at a known position from the drawing. Confirm the DRO reads the expected coordinate within 0.001-0.002″. If it does not, redo the edge-finds with a clean tip and slower approach. Trust verified; cut later.',
        },
      },
    ],
    quiz: [
      {
        id: 'reference-jaw',
        type: 'multiple-choice',
        question: 'Which jaw of a milling vise is the reference for tramming and edge-finding?',
        options: [
          'The movable jaw — it\'s the one that grips the work',
          'The fixed jaw — it does not move and is trammed parallel to X',
          'Both jaws equally — they are precision-ground to match',
          'Neither — the table itself is the reference',
        ],
        correctIndex: 1,
        explanation: 'The fixed jaw is welded or pinned to the vise body and does not move. It is what you tram parallel to X-axis travel, and every edge-find and dimensional reference is relative to it. The movable jaw just supplies clamping pressure.',
      },
      {
        id: 'tbolt-pattern',
        type: 'true-false',
        question: 'Tightening one T-bolt all the way before the other will twist the vise out of tram.',
        options: ['True', 'False'],
        correctIndex: 0,
        explanation: 'True. Fully torquing one side first pulls the vise off-axis as the other side is still loose. Snug both, then alternate small-tighten passes. Always re-check tram after final tightening.',
      },
      {
        id: 'parallels-purpose',
        type: 'multiple-choice',
        question: 'Why use parallels under a workpiece in a mill vise?',
        options: [
          'To increase clamping force',
          'To raise the work into the jaws AND to give it a known flat bottom reference',
          'To insulate the work electrically from the vise',
          'They are only needed for round stock',
        ],
        correctIndex: 1,
        explanation: 'Parallels lift the work into the jaws and provide a known flat reference for the bottom of the work. The vise bottom is not a reference (chips collect there, not necessarily flat). Matched parallels — within 0.0002″ of each other — keep the bottom of the work true.',
      },
      {
        id: 'edge-finder-offset',
        type: 'multiple-choice',
        question: 'You\'ve just found the X-edge of a workpiece using a standard mechanical edge finder with a 0.200″ diameter tip. To set X=0 exactly on the work edge, you should:',
        options: [
          'Zero the DRO immediately — the kick happened at the edge',
          'Move the table 0.100″ away from the work, then zero',
          'Move the table 0.200″ away from the work, then zero',
          'Move the table 0.100″ toward the work, then zero',
        ],
        correctIndex: 1,
        explanation: 'The kick happens when the tip touches the work — but the spindle centerline is 0.100″ (half the tip diameter) away from the edge. Move 0.100″ away (so the spindle moves over the edge) then zero. Your DRO X-zero is now exactly on the workpiece edge.',
      },
      {
        id: 'jaw-lift',
        type: 'multiple-choice',
        question: 'After clamping work in the vise on parallels, both parallels still slide easily under finger pressure. This means:',
        options: [
          'Normal — parallels should be loose for thermal expansion',
          'The clamping pressure is too high — back off',
          'The work has lifted off the parallels and is not seated; tap it down with a soft-face mallet',
          'The parallels are the wrong size',
        ],
        correctIndex: 2,
        explanation: 'The "parallel pinch test" is the most reliable indicator of jaw lift. The movable jaw lifts work slightly as it tightens. If parallels move under finger pressure after clamping, the work is sitting above them — tap the work down with a soft-face mallet until both parallels lock solid.',
      },
    ],
    videos: [
      {
        title: 'How to Tram a Milling Vise',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks tram milling vise',
        description: 'Quinn\'s step-by-step tramming demo with a dial indicator. The first thing to watch when you mount a vise for real.',
      },
      {
        title: 'Edge Finding on a Mill',
        channel: 'mrpete222 / Tubalcain',
        searchQuery: 'Tubalcain mrpete222 edge finder mill how to use',
        description: 'Tubalcain demonstrates both mechanical wiggler-type and electronic edge finders, including the half-diameter offset gotcha.',
      },
    ],
    sources: [
      {
        label: 'MIT 2.007 — Milling Machine Intro / Tramming the Head (PDF)',
        url: 'https://dspace.mit.edu/bitstream/handle/1721.1/56578/2-007Spring-2005/NR/rdonlyres/Mechanical-Engineering/2-007Spring-2005/5DF905BB-E519-4BEB-9101-58D918C19F62/0/intro.pdf',
      },
      {
        label: 'NC State — Tips and Tricks: Tooling and Workholding (PDF)',
        url: 'https://ise.ncsu.edu/processes/wp-content/uploads/sites/11/2013/08/ToolingWorkholding-ES0350.pdf',
      },
      {
        label: 'Wikipedia — Wiggler (tool)',
        url: 'https://en.wikipedia.org/wiki/Wiggler_(tool)',
      },
      {
        label: 'SME — Finding the Edge (industry overview)',
        url: 'https://www.sme.org/technologies/articles/2019/august/finding-the-edge/',
      },
      {
        label: 'UC Riverside ME — Manual Milling Machines SOP (PDF)',
        url: 'https://me.ucr.edu/sites/default/files/2019-05/UCR-ME-SOP-Manual%20Milling%20Machines-v5.pdf',
      },
    ],
    toolsNeeded: [
      'A milling vise that bolts to your mill table',
      'T-slot bolts or studs + step blocks sized for your table\'s T-slots',
      'A soft-face mallet (dead-blow is ideal — shot-filled head reduces bounce)',
      'A matched pair of parallels, sized for your typical workpiece thickness',
      'A 0.0001″ or 0.0005″ dial test indicator with a mount that fits your spindle',
      'A mechanical edge finder (0.200″ tip) or an electronic edge finder',
      'Wrenches for your T-bolts and drawbar',
      'A clean rag and a chip brush',
    ],
    preChecklist: [
      'Mill table clean — chips brushed out of T-slots, no swarf on the work surface',
      'Vise base and table mating surface wiped clean',
      'Bolts, parallels, and clamping hardware staged and within reach',
      'Indicator working — needle moves freely, lens clean, magnetic base solid',
      'Edge finder spindle taper matches your spindle (R8, BT30, etc.)',
      'Spindle stationary; drawbar reachable for tool changes',
      'Workpiece deburred — sharp edges damage the vise jaws and shift the work',
    ],
    commonMistakes: [
      'Tramming the vise with a chip stuck on the fixed jaw face. Wipe the jaw with a clean cloth before indicating.',
      'Fully tightening one T-bolt before the other — pulls the vise off-tram. Always alternate.',
      'Skipping the parallel-pinch test after clamping. Jaw lift puts the work at an angle; the top surface comes out non-parallel to the bottom.',
      'Using mismatched parallels (front taller than back, even by 0.001″). Tilts the work; same lift-off symptom.',
      'Running a mechanical edge finder above 1000 RPM. The tip flies out by centrifugal force and the kick stops being repeatable.',
      'Forgetting the 0.100″ offset for a 0.200″ edge finder. Zeroing the DRO at the moment of kick puts the datum 0.100″ off — every dimension in the part is wrong by that amount.',
    ],
    practiceExercises: [
      {
        id: 'tram-the-vise',
        description: 'Mount your vise loosely. Tram the fixed jaw to under 0.0005″ across its full length using a dial indicator in the spindle. Tighten the T-bolts in a cross pattern. Re-check tram after tightening. Record the final reading.',
        hint: 'If you cannot get under 0.001″, the vise base or the table is dirty, or the vise base is not flat. Stop and clean / inspect before forcing it.',
      },
      {
        id: 'parallels-pinch-test',
        description: 'Mount a workpiece on parallels with moderate vise pressure. Try to slide each parallel by hand. If either moves, tap the work down with a soft-face mallet until both lock. Then apply full vise pressure and re-test. Both should remain locked.',
        hint: 'You can hear the work seat — the dull "thunk" of the mallet on lifted work changes to a sharp "tap" once the work bottoms on the parallels.',
      },
      {
        id: 'edge-find-and-verify',
        description: 'Using your edge finder, locate the left (X) and front (Y) edges of a clamped workpiece. Apply the 0.100″ offset for each. Set DRO X-zero and Y-zero. Traverse to the opposite (right + back) corner of the work and confirm the DRO reads the expected coordinates within 0.001-0.002″.',
        hint: 'The verification traverse is the calibration cue. If the DRO does not match the expected corner position, your edge-find technique is the variable — repeat with a cleaner tip, slower approach, and lower edge-finder RPM.',
      },
    ],
    unlockAfterModule: 'mill-anatomy',
  },
  {
    id: 'mill-cutting-tools',
    number: 5,
    machine: 'mill',
    title: 'Cutting Tools 101 (Mill)',
    subtitle: 'End mills, face mills, fly cutters, slot drills',
    description: 'Mill tools spin; lathe tools sit still. That one change rewrites the whole tool taxonomy — flute count, helix angle, direction, plunge capability, and climb-vs-conventional cut direction all show up. Pick wrong on a mill and you snap a tool; pick right and the cut feels effortless.',
    estimatedTime: '20 min',
    icon: '🪛',
    sections: [
      {
        id: 'end-mills',
        title: 'End mills — the workhorse',
        content: `
          <p>An <strong>end mill</strong> is the everyday mill tool: a cylindrical cutter with cutting edges (<em>flutes</em>) on both the side and (usually) the end. They mount in a collet or end-mill holder in the spindle and remove material as the spindle rotates and the table feeds the work past them.</p>
          <ul>
            <li><strong>Side flutes</strong> cut when the tool feeds parallel to the workpiece surface — the bulk of all milling. The flutes are ground in a helix so the cut is continuous rather than a chopping impact.</li>
            <li><strong>End flutes</strong> cut when the tool is plunged downward into the work (like a drill). Not all end mills have end flutes that reach the center — see the center-cutting note in the next section.</li>
            <li><strong>Right-hand cut, right-hand spiral.</strong> The vast majority of end mills are right-hand cut: they remove material when the spindle rotates clockwise (viewed from above looking down at the work). Left-hand cutters exist but you will rarely need one.</li>
          </ul>
          <p>End mill sizes are typically called out by cutting diameter (the diameter of the flutes) — 1/4″, 3/8″, 1/2″ are the most-used in starter shops. Length-of-cut (LOC) and overall length (OAL) matter for deep cuts vs short-reach work. <a href="https://www.purdue.edu/bidc/resources/metal-working/mill-tooling-guide/" target="_blank" rel="noopener">[Purdue — Mill Tooling Guide]</a></p>
        `,
        callout: {
          type: 'warning',
          title: 'Spindle direction matters',
          text: 'A right-hand end mill in a spindle rotating in reverse does not cut — it just rubs and squeals. Worse, it can grab and snap. Confirm spindle direction (forward = clockwise as you look down) before every cut. If the chip looks wrong, smells burned, or the cutter is rubbing, stop the spindle and check direction first.',
        },
      },
      {
        id: 'flutes',
        title: 'Flutes, helix, and center-cutting',
        content: `
          <p>Flute count is the single biggest variable in end mill selection. Each flute adds a cutting edge per revolution but takes away room for the chip to escape. The right count depends on what you are cutting:</p>
          <ul>
            <li><strong>2-flute end mills</strong> have the largest chip clearance — the gullets between flutes are wide. Ideal for <strong>soft, gummy materials like aluminum and copper</strong> where chips weld to the tool if they cannot escape. 2-flute end mills are almost always <em>center-cutting</em> (see below) so they can also plunge like a drill.</li>
            <li><strong>3-flute end mills</strong> are a compromise — more chip clearance than 4-flute, more cutting edges than 2-flute. Common for general-purpose work in aluminum.</li>
            <li><strong>4-flute end mills</strong> have the least chip clearance but the most cutting edges. Best for <strong>steel and harder materials</strong> where chips break short, and for finishing passes where surface finish matters more than chip volume. Most 4-flute end mills are <em>not</em> center-cutting.</li>
          </ul>
          <p><strong>Center-cutting</strong> means the end flutes extend all the way to the center of the tool, so the tool can plunge straight down like a drill bit. If an end mill is <em>not</em> center-cutting, the very center of the end face has no cutting edge — plunging crushes it into the work and snaps the tool. <a href="https://web.arch.virginia.edu/alar6712/workshopnotes/workshops2011/CNCfabrication/End%20Mills.pdf" target="_blank" rel="noopener">[UVA — End Mills]</a></p>
          <p><strong>Helix angle</strong> is the spiral of the flutes. Standard ~30° helix is the all-purpose default. Higher-helix (45°+) tools shear material more aggressively and give a cleaner finish but cut more "pull" force, which is bad on a light machine. Stick with standard helix until the tool catalog says otherwise.</p>
        `,
        callout: {
          type: 'warning',
          title: 'Never plunge a non-center-cutting end mill',
          text: 'A 4-flute end mill plunged straight down crushes its un-cut center into the work — the tool either snaps or work-hardens the spot so badly that subsequent cuts wreck the cutter. If you need to make a hole, pre-drill it. If you must enter the work with an end mill, ramp in (combined X-Y and Z motion) or use a 2-flute center-cutting tool.',
        },
      },
      {
        id: 'face-mills',
        title: 'Face mills, fly cutters, and surface work',
        content: `
          <p>End mills handle most cuts, but for cleaning up wide flat surfaces (squaring a block top, facing the top of a casting) there are better tools:</p>
          <ul>
            <li><strong>Face mill.</strong> A large-diameter (1.5-3″ typical) shell cutter with multiple replaceable carbide inserts arranged around its periphery. Mounted on a shell-mill arbor that fits the spindle. Takes wide, shallow cuts very efficiently. A 2″ face mill can clean a 1.75″-wide surface in a single pass.</li>
            <li><strong>Fly cutter.</strong> A single-point tool clamped in a large rotating holder, sweeping a large circle. Cheap to make, cheap to run, leaves an excellent finish — but cuts only one edge per revolution so it is slow. Great for finishing the top of a part where finish matters more than time.</li>
            <li><strong>Shell mill.</strong> Similar concept to face mill — replaceable inserts, mounted on an arbor — but with the cutting geometry oriented for full-depth side cuts as well as facing.</li>
          </ul>
          <p>For a starter shop, an end mill set covers 90% of jobs. A face mill or fly cutter is a worthwhile second purchase once you have a project that wants a clean wide top surface.</p>
        `,
      },
      {
        id: 'climb-vs-conventional',
        title: 'Climb vs conventional milling',
        content: `
          <p>The direction you feed the work past a rotating cutter has two valid options, and one of them is dangerous on a manual mill.</p>
          <ul>
            <li><strong>Conventional milling.</strong> The cutter rotates <em>against</em> the feed direction. As each flute engages the work, it starts with zero chip thickness and ends with maximum chip thickness. Cutting force pushes the table <em>away</em> from the cut — backlash in the leadscrew is taken up against the cut, and the table cannot grab and lurch. <strong>This is the safe default on any manual mill.</strong></li>
            <li><strong>Climb milling.</strong> The cutter rotates <em>with</em> the feed direction. Each flute starts at maximum chip thickness and ends at zero. Surface finish is generally better and tool life is longer. <em>But</em> the cutting force pulls the table <em>toward</em> the cut. On a machine with backlash (every manual mill), the cutter can suddenly grab the work and yank the table the distance of the backlash — destroying the part and potentially the operator. <a href="https://www.harveyperformance.com/in-the-loupe/conventional-vs-climb-milling/" target="_blank" rel="noopener">[Harvey Performance — Climb vs Conventional]</a></li>
          </ul>
          <p><strong>The rule on a manual mill:</strong> conventional milling for all roughing and most finishing. Climb milling only when you have a backlash-eliminator (modern ball-screw mills, CNC) or when taking very light finishing passes where the backlash distance is negligible compared to the cut. If in doubt, conventional.</p>
        `,
        callout: {
          type: 'warning',
          title: 'Climb milling on a manual mill can grab the table',
          text: 'The danger is not gradual — it is a sudden lurch of the entire table by the backlash distance, often 0.005-0.020″. That sudden motion can pull a finger into the cutter, snap the tool, ruin the part, or all three. CNC machines with backlash-free ball screws are fine. Manual mills are not. Conventional milling, always, unless you know exactly why you are deviating.',
        },
      },
      {
        id: 'tool-holding',
        title: 'Tool holding — collets, end mill holders, drawbar',
        content: `
          <p>Mill tools mount in the spindle via a tapered tool holder. The two starter options:</p>
          <ul>
            <li><strong>Collets</strong> (R8 collet is Bridgeport standard, ER-style on other mills). A precision-tapered sleeve that grips the tool shank when the drawbar pulls it tight into the spindle. <strong>The collet size must exactly match the tool shank diameter</strong> — a 3/8″ tool in a 1/2″ collet will not grip properly and will pull out or run untrue.</li>
            <li><strong>End mill holders</strong> (Weldon-style). A more rigid holder with a side set-screw that bears on a flat (the "Weldon flat") ground into the tool shank. Holds the tool more rigidly than a collet, does not slip under heavy cuts, but each holder is sized for one specific tool diameter.</li>
          </ul>
          <p><strong>Drawbar etiquette.</strong> The drawbar threads into the top of the collet or holder and pulls it into the spindle taper. Tighten with feel: snug, then 1/4 turn more with a wrench. Cranking down on the drawbar with leverage damages the threads and makes the next tool change painful. Loosen with the wrench, then tap the top of the drawbar with a soft-face mallet to break the taper free. Catch the tool with your other hand.</p>
          <p>Always shut the spindle off and let it stop completely before changing tools. The spindle has more momentum than it looks.</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue — a good cut looks effortless',
          text: 'The right tool, right direction, right speed and feed produce a continuous shower of chips at the cutter, a steady low hum from the spindle, and a clean surface behind the tool. Chip color matches the material (silver-shiny on aluminum, light straw on steel). The cutter is not vibrating; the table is not chattering. If the cut sounds and looks anything other than boring and controlled, stop — usually the tool, direction, or RPM is the variable.',
        },
      },
    ],
    quiz: [
      {
        id: 'climb-vs-conventional-q',
        type: 'multiple-choice',
        question: 'On a manual milling machine with a standard leadscrew (no backlash eliminator), which cut direction is the safe default?',
        options: [
          'Climb milling — better surface finish',
          'Conventional milling — backlash is taken up against the cut',
          'Either — direction does not matter on a manual mill',
          'Climb for steel, conventional for aluminum',
        ],
        correctIndex: 1,
        explanation: 'Conventional milling (cutter rotates against the feed direction) pushes the table away from the cut, taking up leadscrew backlash safely. Climb milling pulls the table toward the cut and can suddenly grab and lurch the work by the backlash distance — dangerous on any machine without a backlash eliminator.',
      },
      {
        id: 'flute-count-aluminum',
        type: 'multiple-choice',
        question: 'You\'re milling a slot in 6061 aluminum. The best flute count for the end mill is:',
        options: [
          '1-flute — maximum chip clearance',
          '2-flute — large gullets clear gummy chips',
          '4-flute — maximum cutting edges',
          '6-flute — finest finish',
        ],
        correctIndex: 1,
        explanation: 'Aluminum is soft and gummy — chips want to weld to the tool unless they can escape quickly. 2-flute end mills have the largest chip gullets, evacuating chips before they re-cut. 4+ flute tools in aluminum slot cuts pack chips and snap.',
      },
      {
        id: 'all-end-mills-plunge',
        type: 'true-false',
        question: 'Any end mill can be plunged straight down into a workpiece like a drill bit.',
        options: ['True', 'False'],
        correctIndex: 1,
        explanation: 'False. Only "center-cutting" end mills (typically 2-flute, and some specifically-marked 3+ flute tools) have cutting edges that reach the center of the tool face. Non-center-cutting tools have an un-cut spot at the very center; plunging crushes it into the work and snaps the cutter. Pre-drill, ramp in, or use a 2-flute center-cutting tool.',
      },
      {
        id: 'collet-size',
        type: 'multiple-choice',
        question: 'You need to mount a 3/8″ end mill but the closest collet you have on hand is 1/2″. The correct action is:',
        options: [
          'Use the 1/2″ collet — it\'s close enough',
          'Wrap the end mill shank with paper or tape to take up the difference',
          'Use a 3/8″ collet — find one or use a 3/8″ end mill holder instead',
          'Use the 1/2″ collet and crank the drawbar extra tight',
        ],
        correctIndex: 2,
        explanation: 'Collets only grip properly at their nominal diameter. An undersized tool in an oversized collet is held loosely — it will pull out under cutting force, run untrue, or chatter badly. Use the right collet or use an end mill holder sized for the tool.',
      },
      {
        id: 'spindle-direction',
        type: 'multiple-choice',
        question: 'You start a cut with a right-hand end mill and the tool just rubs and squeals instead of cutting. The first thing to check is:',
        options: [
          'Increase spindle RPM',
          'Increase feed rate',
          'Spindle direction — verify it is in forward (clockwise viewed from above)',
          'Take a heavier depth of cut',
        ],
        correctIndex: 2,
        explanation: 'A right-hand cutter in a reversed spindle does not cut — the cutting edges face the wrong way. Stop immediately, verify the direction selector is in forward. Cranking up speed or feed against a wrongly-rotating cutter snaps the tool.',
      },
    ],
    videos: [
      {
        title: 'End Mills — Types and Uses',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks end mills types beginner',
        description: 'Quinn\'s overview of end mill geometry, flute counts, and when to grab which tool. The right starting point for sorting out your tool drawer.',
      },
      {
        title: 'Climb vs Conventional Milling Explained',
        channel: 'Joe Pieczynski',
        searchQuery: 'Joe Pieczynski climb conventional milling backlash',
        description: 'Joe Pie demonstrates the danger of climb milling on a manual machine with backlash. The visual is worth a thousand words on why "conventional first" is the rule.',
      },
    ],
    sources: [
      {
        label: 'Harvey Performance — Climb Milling vs. Conventional Milling',
        url: 'https://www.harveyperformance.com/in-the-loupe/conventional-vs-climb-milling/',
      },
      {
        label: 'Purdue — Mill Tooling Guide',
        url: 'https://www.purdue.edu/bidc/resources/metal-working/mill-tooling-guide/',
      },
      {
        label: 'University of Virginia — End Mills (PDF)',
        url: 'https://web.arch.virginia.edu/alar6712/workshopnotes/workshops2011/CNCfabrication/End%20Mills.pdf',
      },
      {
        label: 'NC State — Tips and Tricks: Tooling and Workholding (PDF)',
        url: 'https://ise.ncsu.edu/processes/wp-content/uploads/sites/11/2013/08/ToolingWorkholding-ES0350.pdf',
      },
      {
        label: 'U.S. Army TC 9-524 Ch. 8 — Milling Operations (PDF, hosted by CMU)',
        url: 'https://uhv.cheme.cmu.edu/procedures/machining/ch8.pdf',
      },
    ],
    toolsNeeded: [
      'A starter set of end mills — 1/4″, 3/8″, 1/2″ in both 2-flute and 4-flute is a good baseline',
      'A collet set for your spindle (R8 collet set for Bridgeport, ER-32 or similar for other mills)',
      'End mill holders (Weldon-style) for the sizes you use most often, for rigidity on heavier cuts',
      'A drawbar wrench (or whatever your spindle uses to tighten / loosen the drawbar)',
      'A soft-face mallet for breaking the taper free during tool changes',
      'Optional: a face mill (2-3″) or fly cutter for surfacing wide flats',
      'A chip brush and clean rag',
    ],
    preChecklist: [
      'Spindle OFF and stopped completely before changing any tool',
      'Quill retracted to upper limit and locked',
      'Drawbar wrench at hand; mallet within reach for tap-loose on removal',
      'Collet size matches tool shank diameter exactly (or use an end mill holder of the correct size)',
      'Tool inspected — no chipped flutes, no nicks on the shank, no rust',
      'Right tool picked for the operation (center-cutting for plunges, 2-flute for aluminum slots, etc.)',
      'Spindle direction verified — forward (clockwise from above) for standard right-hand cutters',
    ],
    commonMistakes: [
      'Running the spindle in reverse with a right-hand cutter — the tool rubs, squeals, and can grab. Always verify direction.',
      'Plunging with a non-center-cutting 4-flute end mill — crushes the un-cut center and snaps the tool.',
      'Climb milling on a manual mill with backlash — the table can suddenly lurch the distance of the backlash.',
      'Using a collet whose nominal size does not match the tool shank — tool slips out under load or runs untrue.',
      'Over-tightening the drawbar with cheater leverage — damages threads, makes the next tool change a battle.',
      'Changing tools while the spindle is still spinning down. Wait for full stop before reaching for the drawbar.',
    ],
    practiceExercises: [
      {
        id: 'tool-inventory',
        description: 'Lay out every end mill in your starter set. For each one, identify: diameter, flute count, whether center-cutting, and a one-line "what this is best for." Anything you cannot answer means look it up before using it.',
        hint: 'Most end mills are stamped with diameter and flute count on the shank. Center-cutting status often is not stamped — check the end face: cutting edges reaching all the way to center = center-cutting.',
      },
      {
        id: 'drawbar-drill',
        description: 'Practice mounting and removing the same end mill three times in a row. Note the feel of "snug then 1/4 turn more" for tightening, and the technique of tap-with-mallet-then-catch for removal. Build the habit before you need it under a deadline.',
        hint: 'Tighten with one hand on the drawbar wrench and a finger on the spindle brake — gentle pressure on the brake holds the spindle still while you tighten. Same for loosening.',
      },
      {
        id: 'direction-check-drill',
        description: 'With the spindle OFF, look down at the end mill from above. The flutes spiral one way or the other; the cutting edges point in one direction. Sight which way the tool needs to rotate to cut. Then start the spindle in forward and confirm it matches.',
        hint: 'On a right-hand cutter (the vast majority), the flutes spiral counter-clockwise when viewed from below (looking up at the cutting end). The spindle should rotate clockwise viewed from above. Confirm direction before every cut.',
      },
    ],
    unlockAfterModule: 'mill-workholding',
  },
  {
    id: 'mill-speeds-feeds',
    number: 6,
    machine: 'mill',
    title: 'Speeds & Feeds (Mill)',
    subtitle: 'SFM, RPM, chip load, and rules of thumb that keep you out of trouble',
    description: 'The SFM concept transfers from the lathe, but the math gets one extra term: feed-per-tooth (chip load), because the cutter has multiple flutes instead of one stationary tool. Get the chip load right and the cutter sings; get it wrong and you either rub or snap.',
    estimatedTime: '25 min',
    icon: '📊',
    sections: [
      {
        id: 'sfm-concept-mill',
        title: 'SFM on a mill — same idea, different diameter',
        content: `
          <p>Surface footage (SFM) on a mill works the same way it does on a lathe: it is the speed at which the cutting edge travels across the material, measured in feet per minute. The cutter material wants to run within a particular SFM range; outside that range it either burns (too fast) or rubs (too slow). <a href="https://en.wikipedia.org/wiki/Surface_feet_per_minute" target="_blank" rel="noopener">[Wikipedia — SFPM]</a></p>
          <p><strong>The key difference from the lathe</strong>: on a mill, the rotating part is the <em>tool</em>, not the workpiece. So the diameter that goes into the SFM-to-RPM formula is the <strong>cutter diameter</strong>, not the workpiece diameter:</p>
          <p style="text-align: center; font-size: 1.1rem;"><strong>RPM ≈ (SFM × 4) / D<sub>tool</sub></strong></p>
          <p><strong>Worked example:</strong> 1/2″ HSS end mill in mild steel at 110 SFM. RPM = (110 × 4) / 0.5 = <strong>880 RPM</strong>. Same math, same shortcut as the lathe (12/π ≈ 4); just remember which diameter to plug in.</p>
          <p><strong>Carbide vs HSS</strong> follows the same pattern as the lathe: carbide runs ~2-3× the SFM of HSS in the same material. So that same 1/2″ end mill in mild steel, carbide, would aim for ~250-300 SFM → ~2000-2400 RPM. <a href="https://www.harveyperformance.com/in-the-loupe/speeds-and-feeds-101/" target="_blank" rel="noopener">[Harvey Performance — Speeds and Feeds 101]</a></p>
        `,
        callout: {
          type: 'warning',
          title: 'Feed too low rubs; feed too high snaps',
          text: 'A mill cutter has a narrower "happy" feed band than a lathe tool. Below the band, the cutting edge rubs instead of bites — work-hardens the surface, glazes the cutter, burns out edges. Above the band, the cutter takes too big a chip per tooth, deflects, then snaps. The chip-load math below puts you in the band; the chip and sound keep you there.',
        },
      },
      {
        id: 'sfm-table-mill',
        title: 'Starting SFM values for common materials',
        content: `
          <p>Conservative starting values for milling common materials. These are textbook numbers, meant to keep you out of trouble on a first cut. Bump up once you have heard and seen what works on your machine.</p>
          <p><strong>HSS end mills:</strong></p>
          <ul>
            <li><strong>Mild steel (1018, 1020):</strong> 80-110 SFM</li>
            <li><strong>Free-machining steel (12L14):</strong> 150-200 SFM</li>
            <li><strong>4140 alloy steel:</strong> 50-80 SFM</li>
            <li><strong>Aluminum (6061, 7075):</strong> 200-400 SFM (some HSS finish work goes higher)</li>
            <li><strong>Brass (free-machining):</strong> 200-300 SFM</li>
            <li><strong>Cast iron (gray):</strong> 60-90 SFM</li>
            <li><strong>Stainless 304:</strong> 50-80 SFM (work-hardens easily — run slow)</li>
          </ul>
          <p><strong>Carbide end mills:</strong> roughly 2-3× HSS values. Mild steel carbide: ~250-400 SFM. Aluminum carbide on a stiff machine: 800-1500 SFM. <a href="https://www.cnccookbook.com/hss-end-mill-speeds-and-feeds/" target="_blank" rel="noopener">[CNC Cookbook — HSS End Mill Speeds]</a></p>
          <p>These are <em>starting</em> points. A worn HSS tool runs slower than a fresh one. A rigid mill takes faster cuts than a flimsy one. A flood-coolant setup handles more heat than dry cutting. Adjust based on what you see at the cutter.</p>
        `,
      },
      {
        id: 'chip-load',
        title: 'Chip load — the milling-specific term',
        content: `
          <p>On a lathe, you set feed in <strong>inches per revolution</strong> — one number, because the tool has one cutting edge. On a mill, the cutter has multiple flutes (cutting edges), and feed is set in <strong>inches per minute (IPM)</strong> on the X-axis power feed. The IPM you set has to deliver the right <strong>chip load per tooth (IPT)</strong> — how much material each individual flute removes per revolution.</p>
          <p><strong>The formula:</strong></p>
          <p style="text-align: center; font-size: 1.1rem;"><strong>IPM = IPT × number of flutes × RPM</strong></p>
          <p><strong>Worked example:</strong> the same 1/2″ HSS 4-flute end mill at 880 RPM (from above). Recommended chip load for HSS in mild steel: ~0.002 IPT. So:</p>
          <p style="text-align: center;">IPM = 0.002 × 4 × 880 = <strong>~7 IPM</strong></p>
          <p>Set the X-axis power feed to ~7 IPM and you are at the recommended chip load.</p>
          <p><strong>Starting chip loads (IPT) — small end mills (1/4″ to 1/2″), HSS:</strong></p>
          <ul>
            <li>Mild steel: 0.001-0.003 IPT</li>
            <li>Aluminum: 0.003-0.008 IPT (higher because aluminum is soft)</li>
            <li>Stainless: 0.001-0.002 IPT (lower because heat is the enemy)</li>
          </ul>
          <p>Carbide tools can take 2-3× higher chip loads. Larger-diameter tools take higher chip loads than small ones (they are more rigid). <a href="https://www.harveyperformance.com/in-the-loupe/speeds-and-feeds-101/" target="_blank" rel="noopener">[Harvey Performance — Speeds and Feeds 101]</a></p>
        `,
        callout: {
          type: 'tip',
          title: 'The flute count is not optional in the math',
          text: 'New mill operators sometimes treat IPM the same way they treat lathe IPR — multiply chip load by RPM and call it good. That forgets the flute count, giving you a feed that is half (2-flute) or quarter (4-flute) of what the tool wants. The result: rubbing, glazing, work hardening. Always: chip load × flutes × RPM.',
        },
      },
      {
        id: 'doc',
        title: 'Axial vs radial depth of cut — depth and stepover',
        content: `
          <p>A mill cut has two depth dimensions, not one. The lathe has just "depth of cut" — how deep into the OD. The mill has two:</p>
          <ul>
            <li><strong>Axial depth of cut (ADOC)</strong> — how deep the tool reaches into the work along its centerline (the Z direction). Also called <em>stepdown</em>.</li>
            <li><strong>Radial depth of cut (RDOC)</strong> — how wide the cut is, perpendicular to the tool axis. Also called <em>stepover</em>, <em>cut width</em>, or <em>WOC</em>. <a href="https://www.harveyperformance.com/in-the-loupe/depth-of-cut/" target="_blank" rel="noopener">[Harvey Performance — Depth of Cut]</a></li>
          </ul>
          <p>Both together determine how much material the cutter is engaging. Standard starting rules of thumb:</p>
          <ul>
            <li><strong>Side cut roughing:</strong> ADOC up to 1× cutter diameter, RDOC ~30-50% of cutter diameter. So a 1/2″ end mill: 0.500″ deep × 0.150-0.250″ wide per pass.</li>
            <li><strong>Side cut finishing:</strong> RDOC ~3-5% of cutter diameter. 1/2″ end mill: 0.015-0.025″ stepover. The light finishing pass cleans up the surface without removing much material.</li>
            <li><strong>Slot cut (full-width):</strong> RDOC = cutter diameter (you are cutting a full slot). Cut the feed in half compared to a side cut at the same ADOC — the cutter is engaged on both sides and the chip evacuation gets harder.</li>
          </ul>
          <p>For a starter machine, halve the roughing numbers above. A small benchtop mill is not a Bridgeport; a Bridgeport is not a Haas. Stiffness sets the ceiling on ADOC × RDOC.</p>
        `,
      },
      {
        id: 'listening-mill',
        title: 'Putting it together and listening to the machine',
        content: `
          <p>A full worked example for a typical first-cut setup:</p>
          <ul>
            <li><strong>Material:</strong> 1/2″ × 2″ × 4″ block of 6061 aluminum</li>
            <li><strong>Tool:</strong> 1/2″ 2-flute HSS end mill (center-cutting)</li>
            <li><strong>Target SFM:</strong> 300 (HSS in aluminum, conservative)</li>
            <li><strong>RPM:</strong> (300 × 4) / 0.5 = 2400 RPM. Set to nearest available.</li>
            <li><strong>Chip load:</strong> 0.005 IPT (aluminum, small end mill, HSS)</li>
            <li><strong>IPM:</strong> 0.005 × 2 × 2400 = 24 IPM. Set X power feed to ~24 IPM.</li>
            <li><strong>Cut plan (side cut):</strong> ADOC 0.250″ (half the cutter diameter for a starter setup), RDOC 0.125″ (25% stepover). Conventional milling direction.</li>
            <li><strong>Coolant:</strong> brush-on cutting wax or compressed air for aluminum.</li>
          </ul>
          <p>Then the same listening discipline as the lathe:</p>
          <ul>
            <li><strong>Sound.</strong> Smooth steady hum. High-pitched chatter = back off feed, RPM, or DOC. Squealing = stop.</li>
            <li><strong>Chip.</strong> Aluminum should produce small curls flying off the cutter. Long strings mean too little chip load. Burned dark chips mean RPM too high or feed too low.</li>
            <li><strong>Surface.</strong> Aluminum should leave a shiny machined surface. Smeared / built-up edge means RPM too high or wrong tool geometry; torn means too low feed or dull tool.</li>
          </ul>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue',
          text: 'A correctly run mill cut produces continuous flying chips that match the material color (silver-shiny on aluminum, light straw on steel — never blue or black), a steady mid-range hum from the spindle, no visible chatter marks on the surface, and a finish that does not need a second pass to look presentable. If the chip color goes dark, the tool is past its happy band — slow RPM, increase feed, or both.',
        },
      },
    ],
    quiz: [
      {
        id: 'mill-sfm-diameter',
        type: 'multiple-choice',
        question: 'On a mill, which diameter goes into the SFM-to-RPM formula RPM = (SFM × 4) / D?',
        options: [
          'The workpiece diameter',
          'The cutter (tool) diameter',
          'The largest dimension of the workpiece',
          'The spindle bore diameter',
        ],
        correctIndex: 1,
        explanation: 'On a mill the cutter rotates, so the relevant surface speed is at the cutter\'s outer edge. D in the formula is the tool diameter. This is the opposite of the lathe, where the workpiece rotates and you use workpiece diameter — a common transfer-error from one machine to the other.',
      },
      {
        id: 'ipm-formula',
        type: 'multiple-choice',
        question: 'A 1/2″ 4-flute end mill is spinning at 1000 RPM. You want a chip load of 0.003 IPT. The feed rate (IPM) should be:',
        options: [
          '3 IPM',
          '4 IPM',
          '12 IPM',
          '750 IPM',
        ],
        correctIndex: 2,
        explanation: 'IPM = IPT × flutes × RPM = 0.003 × 4 × 1000 = 12 IPM. The flute count is not optional in the math — forget it and you set 3 IPM (rubbing) or 12 thousand IPM (impossible). The number of flutes matters because each flute takes its own chip per revolution.',
      },
      {
        id: 'slot-feed',
        type: 'true-false',
        question: 'A full-width slot cut can use the same feed rate as a same-depth side cut.',
        options: ['True', 'False'],
        correctIndex: 1,
        explanation: 'False. A slot cut has the cutter engaged on both sides (180° wrap vs ~90° for a typical side cut), doubling the cutting force and making chip evacuation much harder. Typical rule: halve the feed of an equivalent-depth side cut. Better still — pre-drill the start of the slot so the cutter ramps in instead of plunging.',
      },
      {
        id: 'finish-stepover',
        type: 'multiple-choice',
        question: 'For a finishing side-cut pass with a 1/2″ end mill, a typical radial stepover (RDOC) is:',
        options: [
          '0.005 to 0.025″ (1-5% of cutter diameter)',
          '0.050 to 0.100″ (10-20% of cutter diameter)',
          '0.150 to 0.250″ (30-50% of cutter diameter)',
          'Full diameter (slot the rest in one pass)',
        ],
        correctIndex: 0,
        explanation: 'Finishing passes use very light stepover (3-5% of cutter diameter) so the nose of the cutter blends each pass smoothly into the next, producing a clean finish. Heavy stepovers leave visible cusps between passes. The light pass also reduces deflection so the wall is true.',
      },
      {
        id: 'chip-load-meaning',
        type: 'multiple-choice',
        question: 'Chip load (IPT) measures:',
        options: [
          'The mass of chips produced per minute',
          'How much material each individual flute removes per revolution',
          'The total cutting force on the tool',
          'The maximum chip thickness the cutter can handle before breaking',
        ],
        correctIndex: 1,
        explanation: 'Chip load per tooth is exactly what it says: the thickness of material each cutting edge bites per revolution. It is the single most important parameter for tool life on a mill. Too little = rubbing (heat, glazing, work hardening); too much = deflection, breakage. The IPM you set delivers a particular IPT given the RPM and flute count.',
      },
    ],
    videos: [
      {
        title: 'Mill Speeds and Feeds — The Complete Picture',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks mill speeds feeds chip load',
        description: 'Quinn walks through the chip-load math with worked examples. Clear and beginner-friendly.',
      },
      {
        title: 'Reading the Chip on a Mill — When You\'re Wrong',
        channel: 'Joe Pieczynski',
        searchQuery: 'Joe Pieczynski milling chip color feed wrong',
        description: 'Joe Pie shows chip color, sound, and finish for mill cuts that are too slow, too fast, and just right. The visual lesson behind the chart numbers.',
      },
    ],
    sources: [
      {
        label: 'Harvey Performance — Speeds and Feeds 101',
        url: 'https://www.harveyperformance.com/in-the-loupe/speeds-and-feeds-101/',
      },
      {
        label: 'Harvey Performance — Diving Into the Depth of Cut (ADOC / RDOC)',
        url: 'https://www.harveyperformance.com/in-the-loupe/depth-of-cut/',
      },
      {
        label: 'CNC Cookbook — HSS End Mill Speeds and Feeds',
        url: 'https://www.cnccookbook.com/hss-end-mill-speeds-and-feeds/',
      },
      {
        label: 'Wikipedia — Surface Feet Per Minute',
        url: 'https://en.wikipedia.org/wiki/Surface_feet_per_minute',
      },
      {
        label: 'U.S. Army TC 9-524 Ch. 8 — Milling Operations (PDF, hosted by CMU)',
        url: 'https://uhv.cheme.cmu.edu/procedures/machining/ch8.pdf',
      },
    ],
    toolsNeeded: [
      'Your mill with a working spindle speed control and an X-axis power feed',
      'A calculator (or smartphone) for the RPM and IPM math',
      'A speeds-and-feeds chart for the materials and tools you actually use — printed and kept at the mill is great',
      'Your end mill set from the previous module',
      'A piece of practice stock — 6061 aluminum is forgiving for learning; mild steel is the next material to try',
      'Cutting fluid for the material (cutting wax or air for aluminum; cutting oil for steel)',
      'Optional: a chip-load calculator slide rule or a CAM speeds/feeds plugin',
    ],
    preChecklist: [
      'Material identified — you cannot pick SFM for "metal," only for a specific alloy',
      'Tool diameter, tool material (HSS or carbide), and flute count all known',
      'Cut type decided: side cut vs slot, roughing vs finishing',
      'RPM calculated from (SFM × 4) / tool diameter',
      'IPM calculated from IPT × flutes × RPM, set on the X power feed',
      'ADOC (depth) and RDOC (stepover) chosen for the cut type and your machine\'s rigidity',
      'Coolant or air directed at the cut zone if needed',
      'Conventional milling direction confirmed (cutter rotation opposes feed)',
    ],
    commonMistakes: [
      'Using workpiece diameter in the SFM formula (lathe habit) — gives a wildly wrong RPM. On a mill, D = tool diameter.',
      'Forgetting to multiply by flute count in the IPM formula. Gives a feed that is half (2-flute) or quarter (4-flute) of what the tool wants — rubbing and glazing follow.',
      'Slotting at the same feed as a side cut. Halve it. Better still, pre-drill the start.',
      'Stepover too small in roughing (under ~10% of cutter diameter) — the cutter rubs instead of cutting, generates heat, dulls fast.',
      'Stepover too large in finishing (over ~10% of cutter diameter) — leaves visible cusps between passes, fails to clean up walls.',
      'Cranking RPM and feed to maximum on the first cut. Start conservative, watch and listen, then dial up if everything looks good.',
    ],
    practiceExercises: [
      {
        id: 'calculate-ipm',
        description: 'Without a calculator, work out the IPM for: (a) 1/4″ 2-flute HSS in aluminum, 300 SFM, 0.004 IPT — answer: RPM 4800, IPM 38. (b) 1/2″ 4-flute HSS in mild steel, 110 SFM, 0.002 IPT — answer: RPM 880, IPM ~7. (c) 3/8″ 3-flute carbide in 6061 at 800 SFM, 0.005 IPT — answer: RPM 8533, IPM ~128. Verify each on your mill — is the calculated RPM and feed achievable?',
        hint: 'If your machine cannot reach the calculated RPM (older mills top out around 2500-4000 RPM), run at max and accept lower SFM. If your power feed cannot reach the calculated IPM, slow RPM accordingly to keep chip load right.',
      },
      {
        id: 'side-vs-slot',
        description: 'On a piece of scrap, take a side cut at calculated chip-load IPM, then a slot cut at the same depth and half the IPM. Listen to both. The slot cut should be louder and load the spindle more — that is why you halve the feed. Look at the chips: side cut produces clean flying curls; slot cut packs chips into the flutes.',
        hint: 'If you only have a Y-axis hand feed (no power), compute IPM at "X turns per minute" of the handwheel using your handwheel\'s travel-per-turn from mill-anatomy practice. Manual feed is OK if you can hold it steady.',
      },
      {
        id: 'stepover-comparison',
        description: 'Take three side-cut passes at the same depth and feed, but with different stepovers: 50% (roughing), 25% (general purpose), 5% (finishing). Compare the resulting wall surface finish across the three. The light-stepover pass should be visibly smoother — that\'s the finish-pass payoff.',
        hint: 'Use a single piece of stock and step the tool over for each pass — the resulting surface is a step-test showing all three finishes side by side. The fingernail test from the lathe speeds-and-feeds module works here too.',
      },
    ],
    unlockAfterModule: 'mill-cutting-tools',
  },
  {
    id: 'mill-first-cut',
    number: 7,
    machine: 'mill',
    title: 'Your First Cut — Squaring a Block',
    subtitle: 'The six-side dance every machinist learns',
    description: 'The mill capstone: take a rough rectangular blank and end up with six flat, square, parallel sides. Like the lathe first cut, the goal is not the finished part — it is the workflow. Every later mill job (pockets, slots, holes at coordinates) starts from a squared block as the reference geometry.',
    estimatedTime: '45 min',
    icon: '🪨',
    sections: [
      {
        id: 'overview',
        title: 'What "squaring a block" means and why it matters',
        content: `
          <p><strong>Squaring a block</strong> is the procedure of taking a rough rectangular blank — bandsaw-cut, as-cast, or just imperfect — and machining all six sides flat, square (90° to adjacent sides), and parallel to their opposite sides. The result is a known reference geometry: every face is a true plane, every edge is a true line, every corner is a true right angle. <a href="https://kb.wisc.edu/engr/ecbshops/page.php?id=142546" target="_blank" rel="noopener">[U-Wisconsin — Mill Part 1: Squaring Flat Stock]</a></p>
          <p>Once a block is squared, you can:</p>
          <ul>
            <li>Edge-find any side knowing it is a true reference for the others</li>
            <li>Mount the work on any face with confidence the other faces are still oriented correctly</li>
            <li>Layout holes, slots, and pockets from drawing dimensions that assume true edges</li>
          </ul>
          <p>A reasonable hobby-shop target is <strong>±0.003″ on parallelism and squareness</strong>; a careful setup with a good vise gets ±0.001″. Production shops chase ±0.0005″. The point is not the absolute number — it is that you produced a part with <em>known</em> geometry instead of <em>hoped-for</em> geometry.</p>
        `,
        callout: {
          type: 'warning',
          title: 'Deburr between every setup',
          text: 'The most common reason a squared block ends up not-square is a burr or chip stuck between the workpiece and the fixed jaw (or parallels) on a subsequent setup. Each face you cut leaves sharp edges. Each time you flip and re-clamp, run a deburring tool or fine file along every edge of the work first. Otherwise the burr lifts the work, tilts the next cut, and propagates the error.',
        },
      },
      {
        id: 'setup-recap',
        title: 'Setup recap — the six things you already know',
        content: `
          <p>Each previous mill module covered one step. Now they happen back-to-back as one workflow:</p>
          <ul>
            <li><strong>Safety (Module 1).</strong> Glasses on, hair back, sleeves and rings off, closed-toe shoes. E-stop reachable. Spindle direction verified forward.</li>
            <li><strong>Measuring (Module 2).</strong> Caliper and 0-1″ micrometer at hand, zeroed. A precision square — even a 4″ machinist\'s combination square — for squareness checks.</li>
            <li><strong>Mill anatomy (Module 3).</strong> You know which handwheel is X (table) vs Y (saddle) vs Z (knee). Quill retracted and locked for milling. All unused axes locked during each cut.</li>
            <li><strong>Workholding (Module 4).</strong> Vise mounted and trammed to under 0.0005″ across the fixed jaw. Parallels matched and clean. Soft-face mallet within reach.</li>
            <li><strong>Cutting tools (Module 5).</strong> A 2-flute or 3-flute end mill (1/2″ is a good starter size), in a collet that matches the shank, drawbar snug. Conventional milling direction (cutter rotates against feed).</li>
            <li><strong>Speeds and feeds (Module 6).</strong> RPM calculated from tool diameter and material SFM. IPM calculated from chip load × flutes × RPM. Conservative DOC (axial ≤ 0.5× cutter diameter for a starter machine; radial 30-50% for roughing).</li>
          </ul>
          <p>For the worked example below, assume: 1.5″ × 1.5″ × 2.5″ rough aluminum blank → 1.250″ × 1.250″ × 2.000″ finished. 1/2″ 2-flute HSS end mill, ~300 SFM, ~2400 RPM, ~24 IPM, conventional milling.</p>
        `,
      },
      {
        id: 'six-side-dance',
        title: 'The six-side dance — sequence and rationale',
        content: `
          <p>The standard squaring sequence is six setups. The rationale: each setup uses an <em>already-machined</em> face as the reference for the next, so error does not propagate.</p>
          <ol>
            <li><strong>Side 1 (top).</strong> Mount the rough blank in the vise with the largest, flattest-looking face up. Mill the top flat. This becomes your first reference plane.</li>
            <li><strong>Side 2 (bottom, opposite side 1).</strong> Flip 180°. Place side 1 down on the parallels. Tap down with the mallet, pinch-test the parallels. Mill the new top flat. Now sides 1 and 2 are parallel to each other (the parallels are parallel to the spindle Z-zero, so any face you cut on top is parallel to whatever sat on the parallels).</li>
            <li><strong>Side 3 (one edge, perpendicular to 1 and 2).</strong> Rotate 90°. Stand the block on side 2 (or 1), with side 1 (or 2) against the fixed jaw. The fixed jaw is your perpendicular reference. Mill the new top flat. Side 3 is now perpendicular to sides 1-2.</li>
            <li><strong>Side 4 (edge opposite side 3).</strong> Flip 180°. Side 3 down on parallels. Side 1 (or 2) still against fixed jaw. Tap down, pinch-test, mill. Side 4 is now parallel to side 3 and perpendicular to sides 1-2.</li>
            <li><strong>Side 5 (end).</strong> Now the trickier part — the small ends of the block. Stand the block on end (use a 1-2-3 block or a tall pair of parallels to support it). Side 1 against the fixed jaw. Mill the top end.</li>
            <li><strong>Side 6 (other end).</strong> Flip 180°. Side 5 down on parallels (or 1-2-3 block). Mill the last end to final length.</li>
          </ol>
          <p>Between every flip: <strong>deburr every edge with a file or deburr tool</strong>, wipe the vise, wipe the parallels. The mantra: "if a burr is stuck somewhere, the next face won\'t be square."</p>
        `,
        callout: {
          type: 'tip',
          title: 'The fixed jaw is your perpendicularity reference',
          text: 'Sides 3 and 4 (the second pair) become perpendicular to sides 1 and 2 only if the side-against-the-fixed-jaw is dead flat against the jaw. Wipe the jaw face. Use the soft-round-rod trick if needed: place a small (~1/8″) round rod between the moveable jaw and the work — it acts as a hinge that lets the clamping pressure pull the work flush against the fixed jaw rather than just squeezing it from both sides.',
        },
      },
      {
        id: 'walkthrough',
        title: 'Walking each setup — verify, cut, measure',
        content: `
          <p>For each of the six setups, the workflow is the same:</p>
          <ol>
            <li><strong>Deburr</strong> every edge of the work with a file or deburring tool. Wipe the workpiece, the vise jaws, the parallels.</li>
            <li><strong>Mount</strong> the work in the vise with the correct face up. Sides 1-4 sit on parallels; sides 5-6 stand on a 1-2-3 block or tall parallels. Reference face (the most-recently-machined one that matters for this setup) is against the fixed jaw.</li>
            <li><strong>Pinch-test</strong> the parallels. Both should lock under hand pressure after full clamping. If they move, tap with the mallet and re-tighten until both are pinched solid.</li>
            <li><strong>Plan the cut.</strong> How much material to remove this setup? Usually 0.020-0.050″ per pass on a small mill in aluminum. Multiple passes if needed to reach final dimension.</li>
            <li><strong>Set Z-zero</strong> on the top face: lower the end mill until it just touches a feeler gauge or a slip of paper held between the tool and the face. Set DRO Z = 0 (plus paper thickness). Now you can dial in your depth of cut directly.</li>
            <li><strong>Take the cut.</strong> Conventional milling direction (feed against cutter rotation). Lock the unused axes. Use the X power feed at the calculated IPM. Watch the chip and listen.</li>
            <li><strong>Stop the spindle. Measure.</strong> After each pass, mic the dimension you just cut. Decide if another pass is needed. Once the dimension is on target, stop.</li>
            <li><strong>Inspect</strong> the face you just cut: visually flat under shop light, no chatter marks, no tool drag. If the finish is rough, slow down the feed or take another light pass.</li>
          </ol>
          <p>Between sides 2 and 3, run a quick parallelism check: mic the work thickness at four corners. They should agree to within 0.001-0.002″. If they don\'t, one of the first two setups had a burr or a tilt — recheck the parallel-pinch on the next setup. <a href="https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-one-tramming-the-head/" target="_blank" rel="noopener">[Open Oregon — Tramming the Head]</a></p>
        `,
      },
      {
        id: 'inspect-and-learn',
        title: 'Inspect, record, and what good looks like',
        content: `
          <p>After all six sides are cut, the part teaches you what worked and what didn\'t.</p>
          <ul>
            <li><strong>Final dimensions.</strong> Mic each pair of opposite faces. Target: within 0.001-0.003″ of nominal.</li>
            <li><strong>Parallelism.</strong> Measure the thickness at all four corners of each face-pair. The four readings should agree within 0.002″ for a hobby-shop result, 0.0005″ for a careful setup.</li>
            <li><strong>Squareness.</strong> Stand the block on a flat reference (surface plate or known-flat table). Hold a precision square against one face and try to slide a 0.001″ feeler under the square at the top. If it goes through, you\'re out by at least that much.</li>
            <li><strong>Surface finish.</strong> Each face should be uniformly machined with no torn metal, no chatter marks, no remaining saw cut witness. If any face shows worse finish than others, that setup was the variable.</li>
            <li><strong>Notes.</strong> What surprised you? Which setup felt fragile? Which dimension came out tightest? Future-you needs this data more than any textbook chart.</li>
          </ul>
          <p>The squared block lives on as a reference for every subsequent project. Many shops keep their best-ever practice block on a shelf as a benchmark — "I made this in 2026; can I beat it today?"</p>
        `,
        callout: {
          type: 'tip',
          title: 'Calibration cue — the mill-track capstone',
          text: 'A well-squared block looks unremarkable: six flat faces, sharp corners, no visible difference between any two faces. The dimensions check out within a few thou of nominal. Setups felt routine, not panicked. If that describes your block, you have the mill track. If not, the exercise notes are where you figure out which earlier module wants another read.',
        },
      },
    ],
    quiz: [
      {
        id: 'fixed-jaw-role',
        type: 'multiple-choice',
        question: 'When milling sides 3 and 4 of a block (the second pair of opposite faces), what role does the fixed jaw of the vise serve?',
        options: [
          'It applies the clamping pressure',
          'It is the perpendicularity reference — sides 3 and 4 become perpendicular to whichever face is against the fixed jaw',
          'It supports the work\'s weight',
          'It has no specific role on the second pair',
        ],
        correctIndex: 1,
        explanation: 'The fixed jaw is the reference. When side 1 (or 2) is pressed against the fixed jaw, anything milled on the top becomes perpendicular to that face. That\'s why the soft-round-rod trick matters — it ensures the work pulls flush against the fixed jaw rather than tilting under one-sided clamping pressure.',
      },
      {
        id: 'sides-1-2-parallel',
        type: 'true-false',
        question: 'Sides 1 and 2 (the first pair of opposite faces) become parallel to each other automatically, just from being cut sequentially in the same vise.',
        options: ['True', 'False'],
        correctIndex: 0,
        explanation: 'True — assuming you used parallels and seated the work flat on them after flipping. Side 1 cut while on the parallels creates a face parallel to the parallels. After flipping, side 1 sits on the parallels and side 2 is cut on top, parallel to the parallels and therefore parallel to side 1. Parallelism is built into the geometry; you just need to keep the work flat against the parallels (pinch test) on both setups.',
      },
      {
        id: 'soft-round-rod',
        type: 'multiple-choice',
        question: 'Why use a small (~1/8″) round rod between the moveable jaw and the work when clamping for sides 3 / 4?',
        options: [
          'It increases clamping force',
          'It acts as a hinge — clamping pressure pulls the work flush against the fixed jaw rather than just squeezing both sides equally',
          'It prevents the moveable jaw from scratching the freshly-cut face',
          'It is a NIMS certification requirement',
        ],
        correctIndex: 1,
        explanation: 'The round rod is a point-contact pivot. As the moveable jaw advances, the rod\'s line of force pushes the work toward the fixed jaw rather than just squeezing. The freshly-cut face on the fixed-jaw side ends up flush against the jaw — which is the only way sides 3 / 4 come out truly perpendicular to sides 1 / 2.',
      },
      {
        id: 'before-flipping',
        type: 'multiple-choice',
        question: 'Between milling side 1 and flipping the block to mill side 2, the most important thing to do is:',
        options: [
          'Re-tram the vise',
          'Re-zero the DRO X and Y',
          'Deburr the freshly-cut edges with a file or deburring tool',
          'Increase the spindle RPM for the next pass',
        ],
        correctIndex: 2,
        explanation: 'Cutting a face leaves sharp burrs on every edge. If you flip and re-clamp without deburring, a burr stuck between the work and the parallels (or the fixed jaw) tilts the work — every subsequent face is then cut on the tilt. Deburr every edge, every flip. It is the single most common cause of "I followed the procedure and it still came out crooked."',
      },
      {
        id: 'acceptable-tolerance',
        type: 'multiple-choice',
        question: 'A reasonable tolerance target for parallelism and squareness on a first-attempt squared block in a hobby shop is:',
        options: [
          '±0.0001″',
          '±0.0005″',
          '±0.003″',
          '±0.030″',
        ],
        correctIndex: 2,
        explanation: '±0.003″ is a realistic and respectable first-attempt target. A careful setup with a good vise and disciplined deburring can get ±0.001″. Production shops chase ±0.0005″. ±0.0001″ requires surface grinding, not milling. ±0.030″ means something went badly wrong — start over and check vise tram + parallels.',
      },
    ],
    videos: [
      {
        title: 'How to Square a Block on the Milling Machine',
        channel: 'Blondihacks',
        searchQuery: 'Blondihacks square block milling machine',
        description: 'Quinn walks through the full six-side dance with all the details (deburring, parallel-pinch, the soft-rod trick). The right video to watch before your first attempt.',
      },
      {
        title: 'Squaring Stock — The Right Way',
        channel: 'mrpete222 / Tubalcain',
        searchQuery: 'Tubalcain mrpete222 squaring stock milling',
        description: 'Tubalcain\'s shop-class demonstration of squaring stock. Especially good on the why behind each sequence step.',
      },
    ],
    sources: [
      {
        label: 'U-Wisconsin — Intermediate Training Mill Part 1: Squaring Flat Stock',
        url: 'https://kb.wisc.edu/engr/ecbshops/page.php?id=142546',
      },
      {
        label: 'Open Oregon Manufacturing Processes 4-5 — Unit 1: Tramming the Head',
        url: 'https://openoregon.pressbooks.pub/manufacturingprocesses45/chapter/unit-one-tramming-the-head/',
      },
      {
        label: 'MIT 2.007 — Milling Machine Intro / Tramming the Head (PDF)',
        url: 'https://dspace.mit.edu/bitstream/handle/1721.1/56578/2-007Spring-2005/NR/rdonlyres/Mechanical-Engineering/2-007Spring-2005/5DF905BB-E519-4BEB-9101-58D918C19F62/0/intro.pdf',
      },
      {
        label: 'UC Riverside ME — Manual Milling Machines SOP (PDF)',
        url: 'https://me.ucr.edu/sites/default/files/2019-05/UCR-ME-SOP-Manual%20Milling%20Machines-v5.pdf',
      },
      {
        label: 'U.S. Army TC 9-524 Ch. 8 — Milling Operations (PDF, hosted by CMU)',
        url: 'https://uhv.cheme.cmu.edu/procedures/machining/ch8.pdf',
      },
    ],
    toolsNeeded: [
      'Your mill with vise mounted and trammed (Module 4)',
      'A 1/2″ 2-flute or 3-flute HSS end mill, on-center and in a matching collet (Module 5)',
      'A matched pair of parallels',
      'A soft-face mallet (dead-blow ideal)',
      'A piece of round stock about 1/8″ diameter, a few inches long, for the soft-rod trick',
      'A deburring tool or fine file',
      'Caliper, 0-1″ micrometer (Module 2)',
      'A precision square (or at minimum a 4″ machinist\'s combination square)',
      'A 1-2-3 block or tall pair of parallels for standing the work on end (sides 5-6)',
      'Cutting fluid for the material — cutting wax or compressed air for aluminum',
      'A rough rectangular blank — 1.5″ × 1.5″ × 2.5″ aluminum is a good starter; finish target 1.250″ × 1.250″ × 2.000″',
    ],
    preChecklist: [
      'All previous mill-module setup steps complete (safety, measuring tools, vise trammed, tool installed and on speed/feed)',
      'Workpiece roughly trimmed to size (bandsaw cut leaving 0.050-0.100″ on each dimension for cleanup)',
      'A written sequence plan: what face goes against the fixed jaw at each setup, what dimension to reach by the end of each setup',
      'Deburring tool and mallet within easy reach',
      'Parallels clean, free of nicks, matched',
      'Vise fixed-jaw face wiped clean — no chips, no coolant',
      'Spindle direction confirmed forward; conventional milling direction confirmed (feed opposes cutter rotation)',
    ],
    commonMistakes: [
      'Skipping the deburr step between flips. A burr stuck on an edge lifts the work or pushes it off the fixed jaw — every subsequent face inherits the tilt.',
      'Forgetting the parallel-pinch test after re-clamping. Work that lifted off the parallels during clamping comes out angled, not parallel.',
      'Not using the soft-round-rod trick for sides 3-4. Without it, the work can rock between the jaws instead of pressing flush against the fixed jaw, ruining perpendicularity.',
      'Cutting too aggressively on the first face. If you wreck side 1, every later setup loses its reference. First face is the slow careful one.',
      'Re-measuring the same dimension only between flips for that face. Measure parallelism at multiple corners — averaging hides a tilt.',
      'Standing the block on end for sides 5-6 without proper support. A 1-2-3 block or tall parallels is essential; the work cannot sit on the vise bottom and stay straight.',
    ],
    practiceExercises: [
      {
        id: 'square-a-block',
        description: 'Take a rough rectangular aluminum blank (1.5″ × 1.5″ × 2.5″ is a good size) and square it to 1.250″ × 1.250″ × 2.000″ through the six-side sequence. Document each setup: which face is up, which is against the fixed jaw, what depth-of-cut, what the mic reads after the cut. Plan to spend an hour the first time.',
        hint: 'Use aluminum for the first attempt — it cuts easily, finishes well, and is forgiving of a missed deburr. Switch to mild steel once you have the workflow down.',
      },
      {
        id: 'measure-the-result',
        description: 'After all six faces, measure: (a) all three dimensions, (b) parallelism by mic-ing each face pair at all four corners, (c) squareness by holding a precision square against pairs of adjacent faces and looking for daylight under shop light or with a feeler gauge. Record the numbers.',
        hint: 'If parallelism is worse on one face pair than the others, that setup was the variable. The notes from the cut for that setup are where you debug.',
      },
      {
        id: 'reflect',
        description: 'In the notes for this exercise, record: (a) the worst-tolerance face pair and your theory on why, (b) which setup felt the most fragile or improvised, (c) one specific thing you would do differently next time, (d) which earlier module (anatomy, workholding, cutting tools, speeds, etc.) you would re-read if you redid this block tomorrow.',
        hint: 'The "what would I do differently" prompt is the most valuable. The first squared block teaches more than the next ten because everything is new — capture it.',
      },
    ],
    unlockAfterModule: 'mill-speeds-feeds',
  },
];
